import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import {
  Search, Plus, Edit3, Trash2, X, Save, Users, MapPin, Calendar, FileCheck,
  Download, Upload, ArrowUp, ArrowDown, AlertTriangle, Clock, Printer, CheckSquare, Square, Copy,
  Wand2, ChevronLeft, ChevronRight, Merge, LogIn, LogOut, MessageSquare, User, Shield, History,
  Send, Key, UserPlus, Eye, EyeOff, Lock, RotateCw, FileText, Flame, GripVertical, Maximize2, Minimize2, Database
} from "lucide-react";
import { MoveToMonthModal } from "./MoveToMonthModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Production-safe logger: no-ops in production, logs in development
const isDev = process.env.NODE_ENV !== "production";
/* eslint-disable no-console */
const log = {
  error: (...args) => { if (isDev) window.console.error(...args); },
  warn: (...args) => { if (isDev) window.console.warn(...args); },
  debug: (...args) => { if (isDev) window.console.debug(...args); },
};
/* eslint-enable no-console */

// Attach JWT to every request
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem("token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // Only force logout when a strictly-auth endpoint fails.
      // Public-optional endpoints (list_clients, export, duplicates) shouldn't force a re-login
      // if the token is briefly stale; the interceptor will re-attempt on next user action.
      const url = err.config?.url || "";
      const criticalPaths = ["/api/auth/me", "/api/auth/change-password", "/api/users", "/api/audit", "/api/borderou/settings", "/api/sms/templates"];
      const isCritical = criticalPaths.some(p => url.includes(p));
      if (isCritical) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (!window.location.pathname.startsWith("/login")) {
          window.dispatchEvent(new Event("auth:expired"));
        }
      }
    }
    return Promise.reject(err);
  }
);

const COLUMNS = [
  { key: "client", label: "Client", width: 220 },
  { key: "data_verificare_2_ani", label: "Verificare 2 Ani", width: 135, date: true },
  { key: "data_revizie_10_ani", label: "Revizie 10 Ani", width: 130, date: true },
  { key: "adresa_verificare", label: "Adresa Verificare", width: 210 },
  { key: "localitate", label: "Localitate", width: 120 },
  { key: "nr_telefon", label: "Nr Telefon", width: 110 },
  { key: "deg", label: "DEG", width: 100 },
  { key: "contact_suplimentar", label: "Contact Suplimentar", width: 140 },
  { key: "sursa_tabel", label: "Sursa Tabel", width: 170 },
  { key: "observatii", label: "Observatii", width: 190 },
  { key: "email", label: "Email", width: 170 },
  { key: "serie_contor", label: "Serie Contor", width: 120 },
  { key: "data_pif", label: "Data PIF", width: 105, date: true },
  { key: "nr_proiect", label: "Nr Proiect", width: 110 },
  { key: "verificat_centrala", label: "Verificat Centrala", width: 130 },
  { key: "pret_revizie_verificare", label: "Pret Revizie/Verificare", width: 150 },
  { key: "efectuat_verificare", label: "Efectuat Verificare", width: 130, date: true },
  { key: "efectuat_revizie", label: "Efectuat Revizie", width: 125, date: true },
  { key: "nr_sigiliu", label: "Nr. Sigiliu", width: 105 },
];

const DATE_FIELD_OPTIONS = [
  { key: "data_verificare_2_ani", label: "Verificare 2 Ani" },
  { key: "data_revizie_10_ani", label: "Revizie 10 Ani" },
  { key: "data_pif", label: "Data PIF" },
  { key: "efectuat_verificare", label: "Efectuat Verificare" },
  { key: "efectuat_revizie", label: "Efectuat Revizie" },
];

const MONTHS = [
  { v: "1", l: "Ianuarie" }, { v: "2", l: "Februarie" }, { v: "3", l: "Martie" },
  { v: "4", l: "Aprilie" }, { v: "5", l: "Mai" }, { v: "6", l: "Iunie" },
  { v: "7", l: "Iulie" }, { v: "8", l: "August" }, { v: "9", l: "Septembrie" },
  { v: "10", l: "Octombrie" }, { v: "11", l: "Noiembrie" }, { v: "12", l: "Decembrie" },
];

function parseDate(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.trim().match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (!m) return null;
  const [, dd, mm, yy] = m;
  const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
  const d = new Date(year, parseInt(mm, 10) - 1, parseInt(dd, 10));
  if (isNaN(d.getTime())) return null;
  return d;
}

// Frontend equivalent of backend _normalize_name
function normalizeName(s) {
  if (!s) return "";
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Cutoff: only show "Modificat" pill for changes made AFTER this timestamp.
// Set to the moment this feature was deployed so historical updated_at values don't pollute the column.
const MODIFICAT_CUTOFF_TS = new Date("2026-09-30T07:20:00Z").getTime();

const EMPTY_CLIENT = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: "" }), {});

const DEFAULT_SMS_TEMPLATE = "Va informam ca in data de {data_verificare_2_ani} va avea loc verificarea instalatiei de gaz, efectuata de firma Vimato S.R.L.. Pentru programare, va rugam sa transmiteti copia ultimei facturi de gaz: pe WhatsApp la 0728 330 634 sau pe email la scvimato@yahoo.com. Va multumim! Echipa Vimato S.R.L.";
const SMS_TEMPLATES = {
  notificare: {
    label: "Notificare initiala",
    text: DEFAULT_SMS_TEMPLATE,
  },
  reamintire: {
    label: "Reamintire",
    text: "Reamintire: verificarea instalatiei de gaz este programata pe {data_verificare_2_ani}. Va asteptam programarea prin WhatsApp la 0728 330 634 sau email scvimato@yahoo.com. Echipa Vimato S.R.L.",
  },
};
function dateStatus(str) {
  const d = parseDate(str);
  if (!d) return { status: "none" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { status: "expired", daysLeft: diff };
  if (diff <= 30) return { status: "soon", daysLeft: diff };
  return { status: "ok", daysLeft: diff };
}
function rowStatus(row) {
  const a = dateStatus(row.data_verificare_2_ani);
  const b = dateStatus(row.data_revizie_10_ani);
  const order = { expired: 3, soon: 2, ok: 1, none: 0 };
  return order[a.status] >= order[b.status] ? a.status : b.status;
}

function MultiSelectChip({ testid, value, options, onChange, placeholder, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selected = value ? value.split(",").filter(Boolean) : [];
  const toggle = (v) => {
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
    onChange(next.join(","));
  };
  const clearAll = (e) => { e.stopPropagation(); onChange(""); };
  const displayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const opt = options.find(o => String(o.v) === selected[0]);
      return opt ? opt.l : selected[0];
    }
    return `${selected.length} ${label}`;
  };
  return (
    <div className="ms-wrap" ref={ref} data-testid={testid}>
      <button type="button" className={`select select-slim ms-btn ${selected.length ? "on" : ""}`} onClick={() => setOpen(o => !o)}>
        <span className="ms-text">{displayText()}</span>
        {selected.length > 0 && <span className="ms-clear" onClick={clearAll} title="Sterge">×</span>}
        <span className="ms-arrow">▾</span>
      </button>
      {open && (
        <div className="ms-menu">
          {selected.length > 0 && (
            <button type="button" className="ms-clear-all" onClick={() => onChange("")}>Sterge tot ({selected.length})</button>
          )}
          <div className="ms-list">
            {options.map(o => {
              const key = String(o.v);
              const on = selected.includes(key);
              return (
                <label key={key} className={`ms-item ${on ? "on" : ""}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(key)} />
                  <span>{o.l}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AppMain({ currentUser, onLogout, onOpenChangePassword, onOpenUsers, onOpenAudit, onOpenBackups, onOpenPrintHistory }) {
  const [items, setItems] = useState([]);
  // Stable numbering: SHARED across all users via backend.
  // For each filter combo, remember which POSITIONS have been deleted so the visible rows
  // show sequential numbers with gaps at deleted spots (visible to every user).
  const [deletedPosByFilter, setDeletedPosByFilter] = useState({});
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [localitateFilter, setLocalitateFilter] = useState("");
  const [dataField, setDataField] = useState("data_verificare_2_ani");
  const [zi, setZi] = useState("");
  const [luna, setLuna] = useState("");
  const [an, setAn] = useState("");
  const [years, setYears] = useState([]);
  const [localitati, setLocalitati] = useState([]);
  const [page, setPage] = useState(0);
  const [limit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [itemsFilterKey, setItemsFilterKey] = useState("");  // filter key associated with current `items`
  const [stats, setStats] = useState({ total: 0, cu_verificare: 0, cu_revizie: 0, localitati: 0 });
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState("data_verificare_2_ani");
  const [sortOrder, setSortOrder] = useState("asc");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printCols, setPrintCols] = useState(() => new Set(COLUMNS.map(c => c.key)));
  const [printBusy, setPrintBusy] = useState(false);
  // Print customization: user-defined column order and per-column widths (weights)
  const DEFAULT_PRINT_WEIGHTS = {
    client: 22, adresa_verificare: 18, observatii: 16, sursa_tabel: 14,
    email: 14, contact_suplimentar: 12, nr_proiect: 10, verificat_centrala: 10,
    pret_revizie_verificare: 10, serie_contor: 10, nr_sigiliu: 10,
    localitate: 10, nr_telefon: 10, deg: 8,
    data_verificare_2_ani: 9, data_revizie_10_ani: 9, data_pif: 8,
    efectuat_verificare: 9, efectuat_revizie: 9,
  };
  const [printOrder, setPrintOrder] = useState(() => COLUMNS.map(c => c.key));
  const [printWidths, setPrintWidths] = useState(() => ({ ...DEFAULT_PRINT_WEIGHTS }));
  const [printHighlightDup, setPrintHighlightDup] = useState(true);
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);
  const [modifiedToday, setModifiedToday] = useState(false);
  const [modifiedDate, setModifiedDate] = useState("");
  const [borderouClientIds, setBorderouClientIds] = useState(null);
  const [dupCounts, setDupCounts] = useState({});
  const [dupTotal, setDupTotal] = useState(0);
  const [onlyDupNames, setOnlyDupNames] = useState(false);
  const [nameDupCounts, setNameDupCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState(""); // "", "expirat", "scadent", "in_termen"
  const [hasVerificare, setHasVerificare] = useState(false);
  const [hasRevizie, setHasRevizie] = useState(false);
  const [nameDupGroups, setNameDupGroups] = useState(0);
  const [nameDupClients, setNameDupClients] = useState(0);
  const [tableZoom, setTableZoom] = useState(() => {
    const v = parseFloat(localStorage.getItem("tableZoom") || "1");
    return isNaN(v) ? 1 : Math.max(0.6, Math.min(1.8, v));
  });
  useEffect(() => { localStorage.setItem("tableZoom", String(tableZoom)); }, [tableZoom]);
  const [tableFullscreen, setTableFullscreen] = useState(false);
  // ESC exits fullscreen
  useEffect(() => {
    if (!tableFullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setTableFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tableFullscreen]);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupType, setCleanupType] = useState("name"); // 'name' | 'address'
  const [cleanupGroups, setCleanupGroups] = useState([]);
  const [cleanupIdx, setCleanupIdx] = useState(0);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [keepId, setKeepId] = useState(null);
  const [removeIds, setRemoveIds] = useState(new Set());
  const [mergeMode, setMergeMode] = useState("merge"); // 'merge' | 'delete_only'
  const [mergeBusy, setMergeBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [borderouOpen, setBorderouOpen] = useState(false);
  const canEdit = currentUser.role === "admin" || currentUser.role === "tehnician";
  const isAdmin = currentUser.role === "admin";
  const isLaura = (currentUser.username || "").toLowerCase() === "laura";
  const [modifiedByMe, setModifiedByMe] = useState(false);
  const fileInputRef = useRef(null);

  const showToast = useCallback((msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/clients/stats`); setStats(r.data); }
    catch (e) { log.error(e); }
  }, []);

  const loadLocalitati = useCallback(async () => {
    try { const r = await axios.get(`${API}/clients/localitati`); setLocalitati(r.data.localitati); }
    catch (e) { log.error(e); }
  }, []);

  const loadYears = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clients/years`, { params: { data_field: dataField } });
      setYears(r.data.years);
    } catch (e) { log.error(e); }
  }, [dataField]);

  const loadDuplicates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clients/duplicate-addresses`);
      setDupCounts(r.data.counts || {});
      setDupTotal(r.data.total_addresses || 0);
    } catch (e) { log.error(e); }
  }, []);

  const loadDuplicateNames = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clients/duplicate-names`);
      setNameDupCounts(r.data.counts || {});
      setNameDupGroups(r.data.total_groups || 0);
      setNameDupClients(r.data.total_clients || 0);
    } catch (e) { log.error(e); }
  }, []);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients`, {
        params: {
          search, localitate: localitateFilter, zi, luna, an, data_field: dataField,
          only_duplicates: onlyDuplicates,
          only_duplicate_names: onlyDupNames,
          status_filter: statusFilter,
          has_verificare: hasVerificare,
          has_revizie: hasRevizie,
          modified_today: modifiedToday,
          modified_date: modifiedDate,
          modified_by_me: modifiedByMe,
          sort_by: sortBy, sort_order: sortOrder, skip: page * limit, limit,
        },
      });
      setItems(r.data.items);
      setTotal(r.data.total);
      // Tag items with the exact filter combo that produced them (for stable-numbering effect)
      setItemsFilterKey(`${luna}|${an}|${localitateFilter}|${search}|${dataField}|${statusFilter}|${hasVerificare}|${hasRevizie}|${modifiedToday}|${modifiedDate}|${modifiedByMe}|${onlyDuplicates}|${onlyDupNames}|${sortBy}|${sortOrder}`);
    } catch (e) {
      log.error(e);
      showToast("Eroare la incarcare", "err");
    } finally { setLoading(false); }
  }, [search, localitateFilter, zi, luna, an, dataField, onlyDuplicates, onlyDupNames, statusFilter, hasVerificare, hasRevizie, modifiedToday, modifiedDate, modifiedByMe, sortBy, sortOrder, page, limit, showToast]);

  useEffect(() => { loadStats(); loadLocalitati(); loadDuplicates(); loadDuplicateNames(); }, [loadStats, loadLocalitati, loadDuplicates, loadDuplicateNames]);
  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { loadClients(); }, [loadClients]);

  // Fetch shared deleted positions from backend on mount + on filter change (so gaps
  // made by other users show up here too). Skip refetch during in-flight local update.
  const loadDeletedPositions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/deleted-positions`);
      // Normalize positions arrays (backend returns {filter_key: {positions, updated_at,...}})
      const flat = {};
      Object.entries(r.data || {}).forEach(([k, v]) => {
        flat[k] = Array.isArray(v?.positions) ? v.positions : [];
      });
      setDeletedPosByFilter(flat);
      // cleanup any legacy localStorage keys
      try {
        localStorage.removeItem("vimato_deletedPos_v1");
        localStorage.removeItem("vimato_posMap_v1");
        localStorage.removeItem("vimato_posMap_v2");
        localStorage.removeItem("vimato_posMap_v3");
      } catch {}
    } catch (e) { /* silently ignore */ }
  }, []);

  useEffect(() => { loadDeletedPositions(); }, [loadDeletedPositions]);

  // Refresh gaps whenever filter changes (so we get other users' new deletions immediately)
  useEffect(() => {
    loadDeletedPositions();
  }, [luna, an, localitateFilter, dataField, statusFilter, hasVerificare, hasRevizie, modifiedToday, modifiedDate, modifiedByMe, onlyDuplicates, onlyDupNames, sortBy, sortOrder, loadDeletedPositions]);

  // Derive current filter key and the sorted list of deleted positions for it.
  // NOTE: `search` is intentionally excluded so gaps are unified across searches.
  const currentFilterKey = `${luna}|${an}|${localitateFilter}|${dataField}|${statusFilter}|${hasVerificare}|${hasRevizie}|${modifiedToday}|${modifiedDate}|${modifiedByMe}|${onlyDuplicates}|${onlyDupNames}|${sortBy}|${sortOrder}`;
  const deletedPositionsForFilter = useMemo(() => {
    const arr = deletedPosByFilter[currentFilterKey] || [];
    return new Set(arr);
  }, [deletedPosByFilter, currentFilterKey]);

  // Compute displayed row number for each visible row: sequential from 1, skipping deleted positions.
  // Only computed for rows on the current page; walks positions from 1.. taking N-th non-deleted slot.
  const posMap = useMemo(() => {
    const result = {};
    if (!items || items.length === 0) return result;
    const startVisible = page * limit; // number of non-deleted slots before current page
    let n = 1;
    let filled = 0;
    let safety = 0;
    const maxSafety = startVisible + items.length + deletedPositionsForFilter.size + 100000;
    while (filled < startVisible + items.length && safety < maxSafety) {
      if (!deletedPositionsForFilter.has(n)) {
        if (filled >= startVisible) {
          const row = items[filled - startVisible];
          if (row) result[row.id] = n;
        }
        filled++;
      }
      n++;
      safety++;
    }
    return result;
  }, [items, deletedPositionsForFilter, page, limit]);

  // Clear bulk selection when filters change
  useEffect(() => { setSelected(new Set()); }, [search, localitateFilter, zi, luna, an, dataField, onlyDuplicates, onlyDupNames, statusFilter, hasVerificare, hasRevizie, sortBy, sortOrder, page]);

  const toggleSelectRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllPage = () => {
    setSelected(prev => {
      const pageIds = items.map(i => i.id);
      const allOnPage = pageIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allOnPage) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Helper: register deleted positions to shared backend (so other users see the gaps).
  const registerDeletedPositions = useCallback(async (positions) => {
    const pos = (positions || []).filter(p => typeof p === "number");
    if (pos.length === 0) return;
    // Optimistic local update
    setDeletedPosByFilter(prev => {
      const existing = prev[currentFilterKey] || [];
      const merged = Array.from(new Set([...existing, ...pos])).sort((a, b) => a - b);
      return { ...prev, [currentFilterKey]: merged };
    });
    try {
      await axios.post(`${API}/deleted-positions/add`, {
        filter_key: currentFilterKey,
        positions: pos,
      });
    } catch (e) { /* silently ignore; local state already updated */ }
  }, [currentFilterKey]);

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Sterge ${selected.size} clienti selectati? Vor ramane vizibili in rosu pana la finalul anului.`)) return;
    setBulkBusy(true);
    try {
      const positionsToMark = Array.from(selected)
        .map(id => {
          const row = items.find(r => r.id === id);
          return row?._pos ?? posMap[id];
        })
        .filter(p => typeof p === "number");
      const r = await axios.post(`${API}/clients/bulk-delete`, { ids: Array.from(selected) });
      await registerDeletedPositions(positionsToMark);
      const soft = r.data.soft_deleted ?? 0;
      const hard = r.data.hard_deleted ?? 0;
      showToast(soft > 0
        ? `${soft} clienti marcati ca stersi${hard ? ` (+${hard} definitiv)` : ""}`
        : `${r.data.deleted} clienti sterși`);
      clearSelection();
      loadClients(); loadStats(); loadLocalitati(); loadDuplicates(); loadDuplicateNames();
    } catch (e) {
      log.error(e);
      showToast("Eroare la stergere in bloc", "err");
    } finally { setBulkBusy(false); }
  };

  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortOrder("asc"); }
    setPage(0);
  };

  const openEdit = (row) => { setEditing({ ...row }); setIsNew(false); };
  const openNew = () => { setEditing({ ...EMPTY_CLIENT }); setIsNew(true); };
  const closeEdit = () => { setEditing(null); setIsNew(false); };

  const saveClient = async () => {
    try {
      if (isNew) { await axios.post(`${API}/clients`, editing); showToast("Client adaugat"); }
      else { await axios.put(`${API}/clients/${editing.id}`, editing); showToast("Client actualizat"); }
      closeEdit(); loadClients(); loadStats(); loadLocalitati(); loadYears(); loadDuplicates(); loadDuplicateNames();
    } catch (e) { log.error(e); showToast("Eroare la salvare", "err"); }
  };

  const deleteClient = async (row) => {
    if (!window.confirm(`Sterge clientul "${row.client}"?`)) return;
    try {
      const positionToMark = row._pos ?? posMap[row.id];
      await axios.delete(`${API}/clients/${row.id}`);
      if (typeof positionToMark === "number") {
        await registerDeletedPositions([positionToMark]);
      }
      showToast("Client sters"); loadClients(); loadStats(); loadDuplicates(); loadDuplicateNames();
    } catch (e) { showToast("Eroare la stergere", "err"); }
  };

  const exportExcel = async () => {
    try {
      showToast("Se genereaza fisierul…", "ok");
      const r = await axios.get(`${API}/clients/export`, {
        params: {
          search, localitate: localitateFilter, zi, luna, an, data_field: dataField,
          only_duplicates: onlyDuplicates,
          only_duplicate_names: onlyDupNames,
          sort_by: sortBy, sort_order: sortOrder,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      link.setAttribute("download", `clienti_${ts}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      showToast(`Export finalizat (${total} clienti)`);
    } catch (e) { log.error(e); showToast("Eroare la export", "err"); }
  };

  const onImportClick = () => fileInputRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/clients/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      loadClients(); loadStats(); loadLocalitati(); loadYears(); loadDuplicates(); loadDuplicateNames();
    } catch (err) {
      const msg = err.response?.data?.detail || "Eroare la import";
      showToast(msg, "err");
    } finally { setImporting(false); }
  };

  const openCleanup = async (type = "name", focusKey = null) => {
    setCleanupType(type);
    setCleanupOpen(true);
    setCleanupLoading(true);
    setCleanupIdx(0);
    setKeepId(null);
    setRemoveIds(new Set());
    try {
      const r = await axios.get(`${API}/clients/duplicate-groups`, { params: { type } });
      const groups = r.data.groups || [];
      setCleanupGroups(groups);
      if (focusKey) {
        const targetKey = type === "name" ? normalizeName(focusKey) : focusKey;
        const idx = groups.findIndex(g => g.key === targetKey);
        if (idx >= 0) setCleanupIdx(idx);
      }
    } catch (e) {
      log.error(e);
      showToast("Eroare la incarcare grupuri", "err");
    } finally { setCleanupLoading(false); }
  };

  // Initialize keep/remove selection when the current group changes
  useEffect(() => {
    if (!cleanupOpen) return;
    const g = cleanupGroups[cleanupIdx];
    if (!g || !g.items?.length) return;
    // Default: keep the one with most non-empty fields
    const scored = g.items.map(it => ({
      id: it.id,
      score: Object.values(it).filter(v => v && v !== "").length,
    }));
    scored.sort((a, b) => b.score - a.score);
    const kid = scored[0].id;
    setKeepId(kid);
    setRemoveIds(new Set(g.items.filter(x => x.id !== kid).map(x => x.id)));
  }, [cleanupOpen, cleanupIdx, cleanupGroups]);

  const toggleRemove = (id) => {
    if (id === keepId) return;
    setRemoveIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const chooseKeep = (id) => {
    setKeepId(id);
    // Re-check: keep is never in removeIds
    setRemoveIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const doMerge = async () => {
    if (!keepId || removeIds.size === 0) {
      showToast("Selecteaza cel putin un client de sters", "err");
      return;
    }
    const g = cleanupGroups[cleanupIdx];
    const removedCount = removeIds.size;
    if (!window.confirm(`Vei sterge ${removedCount} client(i) din grupul "${g.key}". Confirmi?`)) return;
    setMergeBusy(true);
    try {
      const r = await axios.post(`${API}/clients/merge`, {
        keep_id: keepId,
        remove_ids: Array.from(removeIds),
        mode: mergeMode,
      });
      showToast(`Curatat: ${r.data.deleted} client(i) sterși${r.data.merged_fields ? `, ${r.data.merged_fields} campuri completate` : ""}`);
      // Remove this group locally & advance
      const updated = [...cleanupGroups];
      updated.splice(cleanupIdx, 1);
      setCleanupGroups(updated);
      if (cleanupIdx >= updated.length) setCleanupIdx(Math.max(0, updated.length - 1));
      // Refresh data
      loadClients(); loadStats(); loadLocalitati(); loadDuplicates(); loadDuplicateNames();
    } catch (e) {
      log.error(e);
      showToast(e.response?.data?.detail || "Eroare la merge", "err");
    } finally { setMergeBusy(false); }
  };

  const togglePrintCol = (key) => {
    setPrintCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const setAllPrintCols = (checked) => {
    setPrintCols(checked ? new Set(COLUMNS.map(c => c.key)) : new Set());
  };

  const doPrint = async (autoPrint = true) => {
    if (printCols.size === 0) { showToast("Selecteaza cel putin o coloana", "err"); return; }
    setPrintBusy(true);
    try {
      const r = await axios.get(`${API}/clients`, {
        params: {
          search, localitate: localitateFilter, zi, luna, an, data_field: dataField,
          only_duplicates: onlyDuplicates,
          only_duplicate_names: onlyDupNames,
          status_filter: statusFilter,
          has_verificare: hasVerificare,
          has_revizie: hasRevizie,
          sort_by: sortBy, sort_order: sortOrder, skip: 0, limit: 1000,
        },
      });
      const data = r.data.items;
      // Respect user-defined column order and print inclusion
      const selectedCols = printOrder
        .map(k => COLUMNS.find(c => c.key === k))
        .filter(c => c && printCols.has(c.key));

      // Use user-adjusted widths (weights). Normalize to 100% total.
      const totalWeight = selectedCols.reduce((s, c) => s + (printWidths[c.key] || 10), 0) || 1;
      const colWidth = (key) => `${((printWidths[key] || 10) / totalWeight * 100).toFixed(2)}%`;

      const filterDesc = [];
      if (search) filterDesc.push(`cautare: "${search}"`);
      if (localitateFilter) filterDesc.push(`localitate: ${localitateFilter}`);
      if (luna || an) {
        const dfLabel = DATE_FIELD_OPTIONS.find(d => d.key === dataField)?.label || dataField;
        const mLabel = luna ? MONTHS.find(m => m.v === luna)?.l : "toate lunile";
        filterDesc.push(`${dfLabel}: ${mLabel} ${an || ""}`.trim());
      }
      if (onlyDuplicates) filterDesc.push("doar duplicate pe adresa");
      if (onlyDupNames) filterDesc.push("doar duplicate pe nume");
      const sortLabel = COLUMNS.find(c => c.key === sortBy)?.label || sortBy;
      filterDesc.push(`sortat: ${sortLabel} ${sortOrder === "asc" ? "↑" : "↓"}`);

      const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;" }[c]));
      const now = new Date().toLocaleString("ro-RO");

      // Compact font size based on column count
      const fontSize = selectedCols.length > 12 ? "8px" : selectedCols.length > 8 ? "9px" : "10px";
      const cellPad = selectedCols.length > 12 ? "3px 4px" : "4px 6px";

      // Compute duplicates SCOPED to the current filtered result set (not globally).
      // A client is duplicate in this report if same normalized adresa or normalized name appears >= 2 times.
      const normStr = (v) => String(v || "").trim().toLowerCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
      const addrCounts = {};
      const nameCounts = {};
      data.forEach(r => {
        const a = normStr(r.adresa_verificare);
        const n = normStr(r.client);
        if (a) addrCounts[a] = (addrCounts[a] || 0) + 1;
        if (n) nameCounts[n] = (nameCounts[n] || 0) + 1;
      });
      const dupAddrCount = Object.values(addrCounts).filter(v => v > 1).length;
      const dupNameCount = Object.values(nameCounts).filter(v => v > 1).length;

      // Compute display NR with deletion gaps preserved (same logic as UI table)
      const displayNrs = {};
      {
        let pos = 1;
        let safety = 0;
        const maxSafety = data.length + deletedPositionsForFilter.size + 100000;
        for (let i = 0; i < data.length; i++) {
          while (deletedPositionsForFilter.has(pos) && safety < maxSafety) { pos++; safety++; }
          displayNrs[data[i].id] = pos;
          pos++;
          safety++;
        }
      }

      const rows = data.map((row, i) => {
        const a = normStr(row.adresa_verificare);
        const n = normStr(row.client);
        const isDupAddr = printHighlightDup && a && addrCounts[a] > 1;
        const isDupName = printHighlightDup && n && nameCounts[n] > 1;
        const rowCls = (isDupAddr || isDupName) ? "row-dup" : "";
        return `<tr class="${rowCls}">
        <td class="idx">${displayNrs[row.id] ?? (i + 1)}</td>
        ${selectedCols.map(c => {
          let cls = "";
          if (c.date && row[c.key]) {
            const st = dateStatus(row[c.key]);
            if (st.status === "expired") cls = "expired";
            else if (st.status === "soon") cls = "soon";
          }
          return `<td class="${cls} col-${c.key}">${esc(row[c.key]) || ""}</td>`;
        }).join("")}
      </tr>`;
      }).join("");

      const colgroup = `<colgroup>
        <col class="col-idx"/>
        ${selectedCols.map(c => `<col style="width:${colWidth(c.key)}"/>`).join("")}
      </colgroup>`;

      const html = `<!doctype html><html lang="ro"><head>
        <meta charset="utf-8"/>
        <title>Registru Clienti - Print</title>
        <style>
          @page { size: A4 landscape; margin: 10mm 8mm 14mm 8mm; }
          * { box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #111; margin: 0; padding: 0; font-size: ${fontSize}; }
          .doc { padding: 0; }
          header.doc-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 2px solid #111;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }
          .doc-title { font-size: 14px; font-weight: 700; letter-spacing: 0.2px; }
          .doc-sub { font-size: 10px; color: #555; margin-top: 2px; }
          .doc-meta { font-size: 9px; color: #333; text-align: right; line-height: 1.4; }
          .doc-meta .kv { display: inline-block; }
          .doc-meta b { color: #111; }
          .filter-chip {
            display: inline-block;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 1px 6px;
            margin: 0 2px 2px 0;
            font-size: 8.5px;
            color: #374151;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }
          colgroup .col-idx { width: 26px; }
          th, td {
            border: 1px solid #cbd5e1;
            padding: ${cellPad};
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          th {
            background: #111827;
            color: #fff;
            font-size: 8.5px;
            text-transform: uppercase;
            letter-spacing: 0.25px;
            font-weight: 600;
            border-color: #1f2937;
          }
          tbody tr:nth-child(even) td { background: #f9fafb; }
          tbody tr:hover td { background: #fef3c7; }
          /* Duplicate row highlight - green tint (takes precedence over zebra) */
          tbody tr.row-dup td { background: #dcfce7 !important; }
          tbody tr.row-dup td.col-client, tbody tr.row-dup td.col-adresa_verificare {
            background: #bbf7d0 !important;
            font-weight: 700;
            color: #14532d;
          }
          td.idx {
            text-align: right;
            color: #6b7280;
            font-variant-numeric: tabular-nums;
            font-size: 8px;
            padding: 3px 4px;
          }
          td.expired { color: #991b1b; font-weight: 600; background: #fee2e2 !important; }
          td.soon { color: #92400e; font-weight: 600; background: #fef3c7 !important; }
          /* Special alignment for typical numeric/short fields */
          td.col-data_verificare_2_ani, td.col-data_revizie_10_ani, td.col-data_pif,
          td.col-nr_telefon, td.col-serie_contor, td.col-nr_sigiliu, td.col-pret_revizie_verificare,
          td.col-nr_proiect { text-align: center; font-variant-numeric: tabular-nums; }
          td.col-client { font-weight: 600; }

          footer.doc-foot {
            position: fixed;
            bottom: 4mm;
            left: 8mm;
            right: 8mm;
            display: flex;
            justify-content: space-between;
            font-size: 8px;
            color: #6b7280;
            border-top: 1px solid #d1d5db;
            padding-top: 3px;
          }

          @media screen {
            body { padding: 0; background: #e5e7eb; }
            .preview-toolbar {
              position: sticky;
              top: 0;
              z-index: 200;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 10px 16px;
              background: #ffffff;
              border-bottom: 1px solid #cbd5e1;
              box-shadow: 0 2px 8px rgba(0,0,0,0.06);
              flex-wrap: wrap;
            }
            .pt-title { font-weight: 700; color: #111; font-size: 13px; margin-right: 12px; }
            .pt-spacer { flex: 1; }
            .pt-btn {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              background: #f9fafb;
              color: #111;
              border: 1px solid #d1d5db;
              padding: 6px 12px;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 500;
              font-size: 12px;
              transition: all 0.12s;
            }
            .pt-btn:hover { border-color: #94a3b8; background: #f3f4f6; }
            .pt-btn.primary { background: #d97706; color: #fff; border-color: #b45309; }
            .pt-btn.primary:hover { background: #b45309; }
            .pt-zoom-val {
              min-width: 52px;
              text-align: center;
              font-family: 'JetBrains Mono', ui-monospace, monospace;
              font-weight: 600;
              font-size: 12px;
              color: #111;
              padding: 4px 8px;
              background: #f3f4f6;
              border-radius: 5px;
            }
            .pt-info {
              font-size: 11.5px;
              color: #6b7280;
              font-family: 'JetBrains Mono', monospace;
              padding: 4px 8px;
              background: #ecfdf5;
              border-radius: 5px;
              border: 1px solid #a7f3d0;
              color: #14532d;
            }
            .pt-info.warn { background: #fef3c7; border-color: #fcd34d; color: #78350f; }
            .doc-scroll { padding: 20px; }
            .doc {
              background: white;
              max-width: 297mm;
              margin: 0 auto;
              padding: 12mm 8mm;
              box-shadow: 0 4px 20px rgba(0,0,0,0.15);
              min-height: 210mm;
              transform-origin: top center;
              transition: transform 0.15s ease;
            }
            footer.doc-foot { position: static; margin-top: 20px; }
          }

          @media print {
            body { padding: 0; background: white; }
            .noprint, .preview-toolbar { display: none !important; }
            .doc-scroll { padding: 0; }
            .doc { box-shadow: none; transform: none !important; max-width: none; padding: 0; }
            tr { page-break-inside: avoid; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
        </style>
      </head><body>
        <div class="preview-toolbar noprint">
          <span class="pt-title">🖨 Preview Raport Vimato Instalatii</span>
          <span class="pt-info ${(dupAddrCount + dupNameCount) > 0 ? "" : "warn"}">
            ${(dupAddrCount + dupNameCount) > 0
              ? `● ${dupAddrCount + dupNameCount} grupuri duplicate colorate cu verde in raport`
              : "○ Fara duplicate in setul filtrat"}
          </span>
          <span class="pt-spacer"></span>
          <button class="pt-btn" onclick="zoomOut()" title="Micsoreaza (Ctrl -)">−</button>
          <span class="pt-zoom-val" id="zoomVal">100%</span>
          <button class="pt-btn" onclick="zoomIn()" title="Mareste (Ctrl +)">+</button>
          <button class="pt-btn" onclick="zoomFit()" title="Fit pe latime">⤢ Fit</button>
          <button class="pt-btn" onclick="zoomReset()" title="Reset (Ctrl 0)">100%</button>
          <button class="pt-btn primary" onclick="window.print()">🖨 Printeaza</button>
        </div>
        <div class="doc-scroll">
          <div class="doc" id="docBody">
            <header class="doc-head">
              <div>
                <div class="doc-title">VIMATO INSTALATII · Registru Clienti Gaz</div>
                <div class="doc-sub">Verificari & Revizii · Raport generat automat</div>
              </div>
              <div class="doc-meta">
                <div class="kv"><b>Data:</b> ${esc(now)}</div><br/>
                <div class="kv"><b>Total:</b> ${data.length} inregistrari · <b>${selectedCols.length}</b> coloane · <b>${dupAddrCount + dupNameCount}</b> grupuri duplicate</div>
              </div>
            </header>
            <div style="margin-bottom:8px;">
              ${filterDesc.map(f => `<span class="filter-chip">${esc(f)}</span>`).join("")}
            </div>
            <table>
              ${colgroup}
              <thead><tr>
                <th class="idx">Nr.</th>
                ${selectedCols.map(c => `<th>${esc(c.label)}</th>`).join("")}
              </tr></thead>
              <tbody>${rows}</tbody>
            </table>
            <footer class="doc-foot">
              <span>Vimato Instalatii · Registru Clienti Gaz · exportat ${esc(now)}</span>
              <span>${data.length} clienti · ${selectedCols.length} coloane</span>
            </footer>
          </div>
        </div>
        <script>
          let zoom = 1;
          const doc = document.getElementById('docBody');
          const zoomVal = document.getElementById('zoomVal');
          function applyZoom() {
            doc.style.transform = 'scale(' + zoom + ')';
            zoomVal.textContent = Math.round(zoom * 100) + '%';
          }
          function zoomIn()  { zoom = Math.min(3.0, zoom + 0.1); applyZoom(); }
          function zoomOut() { zoom = Math.max(0.3, zoom - 0.1); applyZoom(); }
          function zoomReset() { zoom = 1; applyZoom(); }
          function zoomFit() {
            const availW = document.querySelector('.doc-scroll').clientWidth - 40;
            const docW = 297 * 3.78; // mm to px approx
            zoom = Math.min(2, Math.max(0.4, availW / docW));
            applyZoom();
          }
          document.addEventListener('keydown', (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomIn(); }
            else if (e.key === '-') { e.preventDefault(); zoomOut(); }
            else if (e.key === '0') { e.preventDefault(); zoomReset(); }
          });
          // Ctrl + wheel to zoom
          document.addEventListener('wheel', (e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              if (e.deltaY < 0) zoomIn(); else zoomOut();
            }
          }, { passive: false });
          ${autoPrint ? "window.addEventListener('load', () => setTimeout(() => window.print(), 400));" : ""}
        </script>
      </body></html>`;

      const win = window.open("", "_blank");
      if (!win) { showToast("Blocare pop-up - permite din browser", "err"); return; }
      // Safe alternative to document.write: use Blob URL
      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);
      win.location.href = blobUrl;
      // Revoke after a delay so the window has time to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      setPrintOpen(false);
    } catch (e) {
      log.error(e);
      showToast("Eroare la printare", "err");
    } finally { setPrintBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageExpiredCount = items.filter(r => rowStatus(r) === "expired").length;
  const pageSoonCount = items.filter(r => rowStatus(r) === "soon").length;

  const activeFilters = [
    search && `cautare`,
    localitateFilter && `localitate`,
    (zi || luna || an) && `data`,
    onlyDuplicates && `dup-adresa`,
    onlyDupNames && `dup-nume`,
  ].filter(Boolean).length;

  return (
    <div className={`app-shell ${tableFullscreen ? "fullscreen-table" : ""}`}>
      <div className="titlebar" data-testid="app-titlebar">
        <div className="tb-dots">
          <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
        </div>
        <div className="tb-title">
          <span className="brand-mini">
            <Flame size={14} strokeWidth={2.4} />
          </span>
          <b>VIMATO INSTALATII</b>
          <span className="tb-sep">·</span>
          <span className="tb-sub">Registru Clienti · Verificari & Revizii Gaz</span>
        </div>
        <div className="tb-right">
          <div className="user-chip" data-testid="user-chip">
            <User size={12} />
            <b>{currentUser.username}</b>
            <span className={`role-badge role-${currentUser.role}`}>{currentUser.role}</span>
          </div>
          {isAdmin && (
            <>
              <button className="tb-btn" onClick={onOpenUsers} title="Utilizatori" data-testid="open-users-btn">
                <Users size={13} /> Useri
              </button>
              <button className="tb-btn" onClick={onOpenAudit} title="Istoric modificari" data-testid="open-audit-btn">
                <History size={13} /> Istoric
              </button>
              <button className="tb-btn" onClick={onOpenPrintHistory} title="Istoric printari borderou (cu redescarcare)" data-testid="open-print-history-btn">
                <Printer size={13} /> Printari
              </button>
              <button className="tb-btn" onClick={onOpenBackups} title="Backup baza de date (zilnic + manual)" data-testid="open-backups-btn">
                <Database size={13} /> Backup
              </button>
            </>
          )}
          <button className="tb-btn" onClick={onOpenChangePassword} title="Schimba parola" data-testid="change-pw-btn">
            <Key size={13} />
          </button>
          <button className="tb-btn danger" onClick={onLogout} title="Deconectare" data-testid="logout-btn">
            <LogOut size={13} />
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="stat-cards">
          <StatCard
            icon={<Users size={16} />} label="Total clienti" value={stats.total} testid="stat-total"
            active={!statusFilter && !hasVerificare && !hasRevizie}
            onClick={() => { setStatusFilter(""); setHasVerificare(false); setHasRevizie(false); setPage(0); }}
          />
          <StatCard
            icon={<FileCheck size={16} />} label="Cu verificare" value={stats.cu_verificare} testid="stat-verificari"
            active={hasVerificare}
            onClick={() => { setHasVerificare(v => !v); setHasRevizie(false); setStatusFilter(""); setPage(0); }}
          />
          <StatCard
            icon={<Calendar size={16} />} label="Cu revizie" value={stats.cu_revizie} testid="stat-revizii"
            active={hasRevizie}
            onClick={() => { setHasRevizie(v => !v); setHasVerificare(false); setStatusFilter(""); setPage(0); }}
          />
          <StatCard icon={<MapPin size={16} />} label="Localitati" value={stats.localitati} testid="stat-localitati" />
        </div>

        <div className="search-bar-row">
          <div className="search-wrap search-wrap-big">
            <Search size={18} className="search-ico" />
            <input
              data-testid="search-input"
              className="search-input search-input-big"
              placeholder="Cauta client · adresa · localitate · telefon · contor · email · nr. proiect · sigiliu…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className="btn-clear" onClick={() => setSearchInput("")} title="Sterge cautarea">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="tb-filters">
          <select data-testid="localitate-filter" className="select" value={localitateFilter} onChange={(e) => { setLocalitateFilter(e.target.value); setPage(0); }}>
            <option value="">Toate localitatile</option>
            {localitati.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>

          {/* Date filter group */}
          <div className="filter-group" title="Filtreaza clientii dupa zi / luna / an pe campul selectat (selecteaza mai multe valori)">
            <span className="fg-label"><Calendar size={13} /></span>
            <select data-testid="data-field-select" className="select select-slim" value={dataField} onChange={(e) => { setDataField(e.target.value); setZi(""); setLuna(""); setAn(""); setPage(0); }}>
              {DATE_FIELD_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <MultiSelectChip
              testid="zi-filter"
              value={zi}
              placeholder="Toate zilele"
              label="zi"
              options={Array.from({ length: 31 }, (_, i) => ({ v: String(i+1), l: String(i+1) }))}
              onChange={(v) => { setZi(v); setPage(0); }}
            />
            <MultiSelectChip
              testid="luna-filter"
              value={luna}
              placeholder="Toate lunile"
              label="luna"
              options={MONTHS}
              onChange={(v) => { setLuna(v); setPage(0); }}
            />
            <MultiSelectChip
              testid="an-filter"
              value={an}
              placeholder="Toti anii"
              label="an"
              options={years.map(y => ({ v: String(y), l: String(y) }))}
              onChange={(v) => { setAn(v); setPage(0); }}
            />
            {(zi || luna || an) && (
              <button data-testid="clear-date-filter" className="btn-clear" onClick={() => { setZi(""); setLuna(""); setAn(""); setPage(0); }} title="Sterge filtrul de data">
                <X size={12} />
              </button>
            )}
          </div>

          <button
            data-testid="toggle-duplicates"
            className={`btn btn-toggle ${onlyDuplicates ? "on" : ""}`}
            onClick={() => { setOnlyDuplicates(v => !v); setPage(0); }}
            title={`${dupTotal} adrese cu duplicate detectate. Click pentru a filtra doar duplicate.`}
          >
            <Copy size={14} /> Dup. adresa
            {dupTotal > 0 && <span className="badge-count">{dupTotal}</span>}
          </button>

          <button
            data-testid="toggle-duplicate-names"
            className={`btn btn-toggle ${onlyDupNames ? "on blue" : ""}`}
            onClick={() => { setOnlyDupNames(v => !v); setPage(0); }}
            title={`Fuzzy match: ${nameDupGroups} grupuri de nume similare, ${nameDupClients} clienti afectati. Ignora diferentele de majuscule, ghilimele, spatii duble si diacritice.`}
          >
            <Users size={14} /> Dup. nume
            {nameDupGroups > 0 && <span className="badge-count">{nameDupGroups}</span>}
          </button>

          <button
            data-testid="download-strict-duplicates"
            className="btn btn-ghost"
            onClick={async () => {
              try {
                showToast("Se genereaza Excel-ul cu dubluri…", "ok");
                const r = await axios.get(`${API}/clients/duplicates-strict/export`, { responseType: "blob" });
                const total = r.headers["x-total-clients"] || "?";
                const grp = r.headers["x-total-groups"] || "?";
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `dubluri_adresa_loc_tel_${new Date().toISOString().slice(0,10)}.xlsx`);
                document.body.appendChild(link); link.click(); link.remove();
                window.URL.revokeObjectURL(url);
                showToast(`Export dubluri: ${grp} grupuri, ${total} clienti`);
              } catch { showToast("Eroare la export dubluri", "err"); }
            }}
            title="Descarca Excel cu toate dublurile care au: aceeasi adresa + aceeasi localitate + acelasi nr. telefon"
          >
            <Copy size={14} /> Dubluri adr+loc+tel
          </button>

          <div className="mod-date-group" data-testid="modified-filter-group">
            <button
              data-testid="toggle-modified-today"
              className={`btn btn-toggle ${modifiedToday ? "on green" : ""}`}
              onClick={() => { setModifiedToday(v => !v); if (!modifiedToday) setModifiedDate(""); setPage(0); }}
              title="Afiseaza doar clientii modificati astazi"
            >
              <Clock size={14} /> Azi
            </button>
            <input
              data-testid="modified-date-picker"
              type="date"
              className="date-input"
              value=""
              onChange={(e) => {
                const d = e.target.value;
                if (!d) return;
                const cur = modifiedDate ? modifiedDate.split(",").filter(Boolean) : [];
                if (!cur.includes(d)) cur.push(d);
                cur.sort();
                setModifiedDate(cur.join(","));
                setModifiedToday(false);
                setPage(0);
              }}
              title="Adauga o zi la filtru (poti alege mai multe)"
              placeholder="Adauga zi"
            />
            {modifiedDate && modifiedDate.split(",").filter(Boolean).map((d) => (
              <span key={d} className="date-chip" data-testid={`date-chip-${d}`}>
                {new Date(d + "T00:00:00").toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })}
                <button
                  className="date-chip-x"
                  data-testid={`date-chip-x-${d}`}
                  onClick={() => {
                    const cur = modifiedDate.split(",").filter(Boolean).filter(x => x !== d);
                    setModifiedDate(cur.join(","));
                    setPage(0);
                  }}
                  title="Elimina ziua"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            {modifiedDate && modifiedDate.split(",").filter(Boolean).length > 1 && (
              <button
                data-testid="clear-all-dates"
                className="clear-date"
                onClick={() => { setModifiedDate(""); setPage(0); }}
                title="Sterge toate zilele"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <button
            data-testid="toggle-modified-by-me"
            className={`btn btn-toggle ${modifiedByMe ? "on green" : ""}`}
            onClick={() => { setModifiedByMe(v => !v); setPage(0); }}
            title="Doar clientii modificati de mine (foloseste calendarul de langa pentru zi specifica)"
          >
            <Users size={14} /> Modificate de mine
          </button>

          {(modifiedToday || modifiedDate || modifiedByMe || onlyDuplicates || onlyDupNames || statusFilter || hasVerificare || hasRevizie || search || localitateFilter || an.length > 0 || zi.length > 0 || luna.length > 0) && total > 0 && (
            <button
              data-testid="btn-export-excel-filter"
              className="btn btn-toggle"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (search) params.set("search", search);
                  if (localitateFilter) params.set("localitate", localitateFilter);
                  if (dataField) params.set("data_field", dataField);
                  if (zi && zi.length) params.set("zi", zi);
                  if (luna && luna.length) params.set("luna", luna);
                  if (an && an.length) params.set("an", an);
                  if (onlyDuplicates) params.set("only_duplicates", "true");
                  if (onlyDupNames) params.set("only_duplicate_names", "true");
                  if (statusFilter) params.set("status_filter", statusFilter);
                  if (hasVerificare) params.set("has_verificare", "true");
                  if (hasRevizie) params.set("has_revizie", "true");
                  if (modifiedToday) params.set("modified_today", "true");
                  if (modifiedDate) params.set("modified_date", modifiedDate);
                  if (modifiedByMe) params.set("modified_by_me", "true");
                  const r = await axios.get(`${API}/clients/export?${params.toString()}`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([r.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.setAttribute("download", `clienti_filtrati_${new Date().toISOString().slice(0,10)}.xlsx`);
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  showToast(`Excel descarcat (${total} clienti)`);
                } catch (e) { showToast("Eroare descarcare Excel", "err"); }
              }}
              title={`Descarca Excel cu toti cei ${total} clienti din filtrele curente`}
            >
              <Download size={14} /> Descarca Excel ({total})
            </button>
          )}

          {(modifiedToday || modifiedDate || modifiedByMe || onlyDuplicates || onlyDupNames || statusFilter || hasVerificare || hasRevizie || search || localitateFilter || an.length > 0 || zi.length > 0 || luna.length > 0) && total > 0 && (
            <button
              data-testid="btn-borderou-from-filter"
              className="btn btn-toggle"
              onClick={async () => {
                try {
                  // Build params, omitting empty/falsy filter values that would fail backend validation
                  const params = {
                    sort_by: sortBy, sort_order: sortOrder,
                    skip: 0, limit: 1000,
                  };
                  if (search) params.search = search;
                  if (localitateFilter) params.localitate = localitateFilter;
                  if (zi && zi.length > 0) params.zi = zi;
                  if (luna && luna.length > 0) params.luna = luna;
                  if (an && an.length > 0) params.an = an;
                  if (dataField) params.data_field = dataField;
                  if (onlyDuplicates) params.only_duplicates = true;
                  if (onlyDupNames) params.only_duplicate_names = true;
                  if (statusFilter) params.status_filter = statusFilter;
                  if (hasVerificare) params.has_verificare = true;
                  if (hasRevizie) params.has_revizie = true;
                  if (modifiedToday) params.modified_today = true;
                  if (modifiedDate) params.modified_date = modifiedDate;
                  if (modifiedByMe) params.modified_by_me = true;

                  // Paginate to bypass backend limit=1000 max
                  let allIds = [];
                  let skip = 0;
                  const pageSize = 1000;
                  while (true) {
                    const r = await axios.get(`${API}/clients`, { params: { ...params, skip, limit: pageSize } });
                    allIds = allIds.concat(r.data.items.map(c => c.id));
                    if (r.data.items.length < pageSize) break;
                    skip += pageSize;
                    if (allIds.length >= 5000) break;  // safety cap
                  }
                  if (allIds.length === 0) { showToast("Niciun client in filtru", "err"); return; }
                  if (allIds.length >= 5000 && total > 5000) {
                    showToast(`Limitat la primii 5000 din ${total} clienti`, "err");
                  }
                  setBorderouClientIds(allIds);
                  setBorderouOpen(true);
                } catch (e) { showToast("Eroare la incarcare", "err"); }
              }}
              title={`Genereaza Borderou predare pentru toti cei ${total} clienti din filtru`}
            >
              <FileText size={14} /> Borderou din filtru ({total})
            </button>
          )}
        </div>

        <div className="tb-actions">
          <div className="filter-badge">
            {activeFilters > 0 ? <><b>{activeFilters}</b> filtru{activeFilters > 1 ? "e" : ""} activ{activeFilters > 1 ? "e" : ""} · <b>{total}</b> rezultate</> : <>Total: <b>{total}</b> clienti</>}
          </div>
          <div style={{ flex: 1 }} />
          <button data-testid="print-btn" className="btn btn-ghost" onClick={() => setPrintOpen(true)} title="Printeaza coloane selectate">
            <Printer size={15} /> Print
          </button>
          <button data-testid="export-btn" className="btn btn-ghost" onClick={exportExcel} title="Exporta datele filtrate in Excel">
            <Download size={15} /> Export
          </button>
          <button data-testid="import-btn" className="btn btn-ghost" onClick={onImportClick} disabled={importing || !canEdit} title={canEdit ? "Importa/actualizeaza din Excel" : "Rol viewer - fara permisiuni"}>
            <Upload size={15} /> {importing ? "Se importa…" : "Import"}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" data-testid="import-file-input" style={{ display: "none" }} onChange={onImportFile} />
          <button data-testid="move-month-btn" className="btn btn-ghost" onClick={() => setMoveOpen(true)} disabled={!canEdit} title="Mută/adaugă clienții din Excel într-o lună">
            <Calendar size={15} /> Mută în lună
          </button>
          <button data-testid="add-client-btn" className="btn btn-primary" onClick={openNew} disabled={!canEdit} title={canEdit ? "" : "Rol viewer - fara permisiuni"}>
            <Plus size={15} /> Client nou
          </button>
        </div>

        <div className="legend">
          <button
            className={`lg-item lg-btn ${statusFilter === "expirat" ? "on" : ""}`}
            data-testid="filter-expirat"
            onClick={() => { setStatusFilter(s => s === "expirat" ? "" : "expirat"); setHasVerificare(false); setHasRevizie(false); setPage(0); }}
            title="Filtreaza clientii cu data expirata"
          >
            <span className="lg-dot expired" /> Expirat
          </button>
          <button
            className={`lg-item lg-btn ${statusFilter === "scadent" ? "on" : ""}`}
            data-testid="filter-scadent"
            onClick={() => { setStatusFilter(s => s === "scadent" ? "" : "scadent"); setHasVerificare(false); setHasRevizie(false); setPage(0); }}
            title="Filtreaza clientii scadenti in <= 30 zile"
          >
            <span className="lg-dot soon" /> Scadent &lt;= 30 zile
          </button>
          <button
            className={`lg-item lg-btn ${statusFilter === "in_termen" ? "on" : ""}`}
            data-testid="filter-in-termen"
            onClick={() => { setStatusFilter(s => s === "in_termen" ? "" : "in_termen"); setHasVerificare(false); setHasRevizie(false); setPage(0); }}
            title="Filtreaza clientii in termen (> 30 zile)"
          >
            <span className="lg-dot ok" /> In termen
          </button>
          <span className="lg-item" title="Randuri colorate cu verde = duplicate (adresa sau nume)">
            <span className="lg-dot dup" /> Duplicate
          </span>
          {statusFilter && (
            <span className="lg-info lg-info-active">
              <b>{total}</b> clienti · <button className="lg-clear" onClick={() => setStatusFilter("")}>x sterge filtru</button>
            </span>
          )}
          {!statusFilter && (pageExpiredCount > 0 || pageSoonCount > 0) && (
            <span className="lg-info">
              <AlertTriangle size={13} /> Pe pagina curenta: <b>{pageExpiredCount}</b> expirat(e), <b>{pageSoonCount}</b> scadent(e) &le; 30 zile
            </span>
          )}
        </div>
      </div>

      <div className="table-wrap" data-testid="clients-table" style={{ "--grid-zoom": tableZoom }}>
        {selected.size > 0 && (
          <div className="bulk-bar" data-testid="bulk-bar">
            <div className="bulk-info">
              <CheckSquare size={16} />
              <b>{selected.size}</b> clienti selectati
              <button className="bulk-clear" onClick={clearSelection} data-testid="bulk-clear">
                Anuleaza selectia
              </button>
            </div>
            <div className="bulk-actions">
              <button
                data-testid="bulk-borderou-btn"
                className="btn btn-primary"
                onClick={() => setBorderouOpen(true)}
                title="Genereaza Borderou predare (Anexa 2)"
              >
                <FileText size={14} /> Borderou predare
              </button>
              <button
                data-testid="bulk-sms-btn"
                className="btn btn-primary"
                onClick={() => setSmsOpen(true)}
                disabled={!canEdit}
                title={canEdit ? "Trimite SMS catre clientii selectati" : "Rol viewer - fara permisiuni"}
              >
                <MessageSquare size={14} /> Trimite SMS
              </button>
              <button
                data-testid="bulk-delete-btn"
                className="btn btn-danger"
                onClick={bulkDelete}
                disabled={bulkBusy || !canEdit}
              >
                <Trash2 size={14} /> {bulkBusy ? "Se sterge…" : `Sterge ${selected.size} clienti`}
              </button>
            </div>
          </div>
        )}
        <div className="table-scroll">
          <table className="grid">
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: 50 }} />
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="col-check" style={{ width: 36, minWidth: 36 }}>
                  <input
                    type="checkbox"
                    data-testid="select-all"
                    checked={items.length > 0 && items.every(i => selected.has(i.id))}
                    onChange={toggleSelectAllPage}
                    title="Selecteaza toata pagina"
                  />
                </th>
                <th className="col-index" style={{ width: 50, minWidth: 50 }}>Nr.</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} style={{ minWidth: c.width, width: c.width }}
                    className={`sortable col-${c.key} ${sortBy === c.key ? "active" : ""}`}
                    onClick={() => toggleSort(c.key)}
                    data-testid={`th-${c.key}`}>
                    <span className="th-inner">
                      {c.label}
                      {sortBy === c.key && (sortOrder === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                    </span>
                  </th>
                ))}
                <th className="col-actions">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLUMNS.length + 3} className="empty">Se incarca…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={COLUMNS.length + 3} className="empty">Nu au fost gasiti clienti.</td></tr>
              ) : items.map((row, idx) => {
                const st = rowStatus(row);
                const isSelected = selected.has(row.id);
                const isDup = (row.adresa_verificare && dupCounts[row.adresa_verificare]) || (row.client && nameDupCounts[row.client]);
                return (
                  <tr key={row.id} data-testid={`client-row-${row.id}`}
                      className={`row-${st} ${isSelected ? "selected" : ""} ${isDup ? "row-duplicate" : ""}`}>
                    <td className="col-check">
                      <input
                        type="checkbox"
                        data-testid={`select-${row.id}`}
                        checked={isSelected}
                        onChange={() => toggleSelectRow(row.id)}
                      />
                    </td>
                    <td className="col-index">{row._pos ?? posMap[row.id] ?? (page * limit + idx + 1)}</td>
                    {COLUMNS.map((c) => {
                      let cellClass = "";
                      let icon = null;
                      if (c.date && row[c.key]) {
                        const s = dateStatus(row[c.key]);
                        cellClass = `cell-${s.status}`;
                        if (s.status === "expired") icon = <AlertTriangle size={12} />;
                        else if (s.status === "soon") icon = <Clock size={12} />;
                      }
                      const isDupAddr = c.key === "adresa_verificare" && row[c.key] && dupCounts[row[c.key]];
                      const isDupName = c.key === "client" && row[c.key] && nameDupCounts[row[c.key]];
                      return (
                        <td key={c.key} className={`col-${c.key} ${cellClass} ${c.key === "client" ? "cell-scroll" : ""}`} style={{ width: c.width, minWidth: c.width, maxWidth: c.width }} title={row[c.key]}>
                          {row[c.key] ? (
                            <span className="cell-inner">
                              {icon}
                              <span>{row[c.key]}</span>
                              {isDupAddr && (
                                <button
                                  className="dup-badge dup-btn"
                                  data-testid={`dup-badge-${row.id}`}
                                  title={`Aceasta adresa apare la ${isDupAddr} clienti. Click pentru a curata.`}
                                  onClick={(e) => { e.stopPropagation(); openCleanup("address", row.adresa_verificare); }}
                                >
                                  <Copy size={10} /> {isDupAddr}
                                </button>
                              )}
                              {isDupName && (
                                <button
                                  className="dup-badge dup-name dup-btn"
                                  data-testid={`dup-name-badge-${row.id}`}
                                  title={`Fuzzy match: ${isDupName - 1} alti clienti cu nume similar. Click pentru a curata.`}
                                  onClick={(e) => { e.stopPropagation(); openCleanup("name", row.client); }}
                                >
                                  <Users size={10} /> {isDupName}
                                </button>
                              )}
                            </span>
                          ) : <span className="cell-inner"><span className="muted">—</span></span>}
                        </td>
                      );
                    })}
                    <td className="col-actions">
                      <button data-testid={`view-${row.id}`} className="icon-btn" onClick={() => setViewing(row)} title="Vezi detalii"><Eye size={14} /></button>
                      <button data-testid={`edit-${row.id}`} className="icon-btn" onClick={() => openEdit(row)} title="Editeaza"><Edit3 size={14} /></button>
                      <button data-testid={`delete-${row.id}`} className="icon-btn danger" onClick={() => deleteClient(row)} title="Sterge"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="page-info">
            {total > 0 ? (
              <>Afisez <b>{page * limit + 1}</b>–<b>{Math.min((page + 1) * limit, total)}</b> din <b>{total}</b></>
            ) : "Niciun rezultat"}
          </div>
          <div className="table-zoom" data-testid="table-zoom">
            <button
              className="tz-btn"
              onClick={() => setTableZoom(z => Math.max(0.6, +(z - 0.1).toFixed(2)))}
              disabled={tableZoom <= 0.6}
              title="Micsoreaza tabelul"
              data-testid="zoom-out"
            >−</button>
            <button
              className="tz-btn tz-reset"
              onClick={() => setTableZoom(1)}
              title="Reset zoom (100%)"
              data-testid="zoom-reset"
            >{Math.round(tableZoom * 100)}%</button>
            <button
              className="tz-btn"
              onClick={() => setTableZoom(z => Math.min(1.8, +(z + 0.1).toFixed(2)))}
              disabled={tableZoom >= 1.8}
              title="Mareste tabelul"
              data-testid="zoom-in"
            >+</button>
            <span className="tz-sep" />
            <button
              className="tz-btn tz-fs"
              onClick={() => setTableFullscreen(v => !v)}
              title={tableFullscreen ? "Iesi din ecran complet (ESC)" : "Tabel pe tot ecranul"}
              data-testid="fullscreen-toggle"
            >
              {tableFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
          <div className="page-controls">
            <button data-testid="page-prev" className="btn btn-ghost" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Anterior</button>
            <span className="page-num">Pagina {page + 1} / {totalPages}</span>
            <button data-testid="page-next" className="btn btn-ghost" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Urmator ›</button>
          </div>
        </div>
      </div>

      {viewing && (
        <div className="modal-backdrop" onClick={() => setViewing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="view-modal">
            <div className="modal-header">
              <div>
                <div className="modal-title"><Eye size={16} style={{ verticalAlign: "-2px" }}/> Detalii client</div>
                <div className="modal-sub">{viewing.client || "—"}</div>
              </div>
              <button className="icon-btn" onClick={() => setViewing(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="view-grid">
                {COLUMNS.map((c) => (
                  <div key={c.key} className={c.key === "observatii" || c.key === "adresa_verificare" ? "view-field span-2" : "view-field"}>
                    <span className="view-lbl">{c.label}</span>
                    <span className="view-val">{viewing[c.key] || <span className="muted">—</span>}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <div style={{flex: 1}} />
              <button className="btn btn-ghost" onClick={() => setViewing(null)}>Inchide</button>
              <button className="btn btn-primary" onClick={() => { const r = viewing; setViewing(null); openEdit(r); }}><Edit3 size={15} /> Editeaza</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={closeEdit}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">{isNew ? "Client nou" : "Editare client"}</div>
                <div className="modal-sub">{editing.client || "—"}</div>
              </div>
              <button className="icon-btn" onClick={closeEdit}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                {COLUMNS.map((c) => (
                  <label key={c.key} className={c.key === "observatii" || c.key === "adresa_verificare" ? "field span-2" : "field"}>
                    <span className="lbl">{c.label}</span>
                    {c.key === "observatii" ? (
                      <textarea data-testid={`field-${c.key}`} rows={2} value={editing[c.key] || ""} onChange={(e) => setEditing({ ...editing, [c.key]: e.target.value })} />
                    ) : (
                      <input data-testid={`field-${c.key}`} type="text" value={editing[c.key] || ""} onChange={(e) => setEditing({ ...editing, [c.key]: e.target.value })} />
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              {!isNew && (
                <button
                  data-testid="delete-from-modal-btn"
                  className="btn btn-danger"
                  onClick={async () => {
                    if (!window.confirm(`Sterge definitiv clientul "${editing.client}"?`)) return;
                    try {
                      const positionToMark = editing._pos ?? posMap[editing.id];
                      await axios.delete(`${API}/clients/${editing.id}`);
                      if (typeof positionToMark === "number") {
                        await registerDeletedPositions([positionToMark]);
                      }
                      showToast("Client sters");
                      closeEdit(); loadClients(); loadStats(); loadDuplicates(); loadDuplicateNames();
                    } catch (e) { showToast("Eroare la stergere", "err"); }
                  }}
                >
                  <Trash2 size={15} /> Sterge client
                </button>
              )}
              <div style={{flex: 1}} />
              <button className="btn btn-ghost" onClick={closeEdit}>Anuleaza</button>
              <button data-testid="save-client-btn" className="btn btn-primary" onClick={saveClient}><Save size={15} /> Salveaza</button>
            </div>
          </div>
        </div>
      )}

      {moveOpen && (
        <MoveToMonthModal api={API} showToast={showToast} onClose={() => setMoveOpen(false)}
          onDone={() => { loadClients(); loadStats(); loadLocalitati(); loadYears(); loadDuplicates(); loadDuplicateNames(); }} />
      )}

      {importResult && (
        <div className="modal-backdrop" onClick={() => setImportResult(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} data-testid="import-result-modal">
            <div className="modal-header">
              <div className="modal-title">Import finalizat</div>
              <button className="icon-btn" onClick={() => setImportResult(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="import-stats">
                <div className="istat"><div className="istat-lbl">Adaugati</div><div className="istat-val ok">{importResult.inserted}</div></div>
                <div className="istat"><div className="istat-lbl">Actualizati</div><div className="istat-val soon">{importResult.updated}</div></div>
                <div className="istat"><div className="istat-lbl">Ignorati</div><div className="istat-val muted-v">{importResult.skipped}</div></div>
              </div>
              <p className="import-help">
                Cheia de identificare: <b>Client + Adresa Verificare + Localitate</b>.<br/>
                Randurile existente au fost <b>actualizate</b> doar cu campurile ne-goale; cele noi <b>adaugate</b>.
              </p>
            </div>
            <div className="modal-footer">
              <button data-testid="import-close-btn" className="btn btn-primary" onClick={() => setImportResult(null)}>Am inteles</button>
            </div>
          </div>
        </div>
      )}

      {printOpen && (
        <div className="modal-backdrop" onClick={() => setPrintOpen(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="print-modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">Printeaza registrul</div>
                <div className="modal-sub">Selecteaza, reordoneaza si ajusteaza latimea coloanelor</div>
              </div>
              <button className="icon-btn" onClick={() => setPrintOpen(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="print-toolbar">
                <button className="btn btn-ghost btn-sm" onClick={() => setAllPrintCols(true)} data-testid="print-select-all">Selecteaza toate</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setAllPrintCols(false)} data-testid="print-deselect-all">Deselecteaza toate</button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { setPrintOrder(COLUMNS.map(c => c.key)); setPrintWidths({ ...DEFAULT_PRINT_WEIGHTS }); }}
                  data-testid="print-reset-order"
                  title="Reseteaza ordinea si latimile la valorile implicite"
                >
                  <RotateCw size={13} /> Reset ordine + latimi
                </button>
                <label className="print-toggle" title="Coloreaza cu verde randurile clientilor duplicati (dupa adresa sau nume)">
                  <input
                    type="checkbox"
                    checked={printHighlightDup}
                    onChange={(e) => setPrintHighlightDup(e.target.checked)}
                    data-testid="print-highlight-dup"
                  />
                  <span>Coloreaza duplicati (verde)</span>
                </label>
                <span className="print-count">{printCols.size} / {COLUMNS.length} coloane</span>
              </div>

              <div className="print-cols-table" data-testid="print-cols-table">
                <div className="pct-head">
                  <span className="pct-h-drag"></span>
                  <span className="pct-h-idx">Nr.</span>
                  <span className="pct-h-check"></span>
                  <span className="pct-h-label">Coloana</span>
                  <span className="pct-h-width">Latime</span>
                  <span className="pct-h-move">Ordine</span>
                </div>
                {printOrder.map((key, idx) => {
                  const c = COLUMNS.find(col => col.key === key);
                  if (!c) return null;
                  const checked = printCols.has(c.key);
                  const w = printWidths[c.key] ?? 10;
                  const moveUp = () => {
                    if (idx === 0) return;
                    const next = [...printOrder];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setPrintOrder(next);
                  };
                  const moveDown = () => {
                    if (idx === printOrder.length - 1) return;
                    const next = [...printOrder];
                    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                    setPrintOrder(next);
                  };
                  const reorderDrop = () => {
                    if (!dragKey || dragKey === c.key) { setDragKey(null); setDragOverKey(null); return; }
                    const from = printOrder.indexOf(dragKey);
                    const to = printOrder.indexOf(c.key);
                    if (from < 0 || to < 0) { setDragKey(null); setDragOverKey(null); return; }
                    const next = [...printOrder];
                    next.splice(from, 1);
                    next.splice(to, 0, dragKey);
                    setPrintOrder(next);
                    setDragKey(null); setDragOverKey(null);
                  };
                  const isDragging = dragKey === c.key;
                  const isOver = dragOverKey === c.key && dragKey && dragKey !== c.key;
                  return (
                    <div
                      key={c.key}
                      className={`pct-row ${checked ? "on" : "off"} ${isDragging ? "dragging" : ""} ${isOver ? "drag-over" : ""}`}
                      data-testid={`print-row-${c.key}`}
                      draggable
                      onDragStart={(e) => {
                        setDragKey(c.key);
                        e.dataTransfer.effectAllowed = "move";
                        try {
                          e.dataTransfer.setData("text/plain", c.key);
                        } catch (err) {
                          // Some Firefox versions throw on setData during drag - ignore, drag still works
                          log.debug("dataTransfer.setData not supported:", err.message);
                        }
                      }}
                      onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverKey !== c.key) setDragOverKey(c.key); }}
                      onDragLeave={() => { if (dragOverKey === c.key) setDragOverKey(null); }}
                      onDrop={(e) => { e.preventDefault(); reorderDrop(); }}
                    >
                      <span className="pct-drag" title="Trage pentru a reordona"><GripVertical size={14} /></span>
                      <span className="pct-idx">{idx + 1}</span>
                      <label className="pct-check">
                        <input type="checkbox" checked={checked} onChange={() => togglePrintCol(c.key)} data-testid={`print-toggle-${c.key}`} />
                        <span className="pct-check-ico">{checked ? <CheckSquare size={14} /> : <Square size={14} />}</span>
                      </label>
                      <span className="pct-label">{c.label}</span>
                      <span className="pct-width">
                        <button
                          type="button"
                          className="pct-w-btn"
                          onClick={() => setPrintWidths(prev => ({ ...prev, [c.key]: Math.max(2, (prev[c.key] || 10) - 1) }))}
                          disabled={!checked}
                          title="Micsoreaza latimea"
                        >−</button>
                        <input
                          type="number"
                          min={2}
                          max={60}
                          value={w}
                          onChange={(e) => setPrintWidths(prev => ({ ...prev, [c.key]: Math.max(2, Math.min(60, Number(e.target.value) || 10)) }))}
                          disabled={!checked}
                          className="pct-w-input"
                          data-testid={`print-width-${c.key}`}
                        />
                        <button
                          type="button"
                          className="pct-w-btn"
                          onClick={() => setPrintWidths(prev => ({ ...prev, [c.key]: Math.min(60, (prev[c.key] || 10) + 1) }))}
                          disabled={!checked}
                          title="Mareste latimea"
                        >+</button>
                      </span>
                      <span className="pct-move">
                        <button type="button" className="pct-m-btn" onClick={moveUp} disabled={idx === 0} title="Muta in sus" data-testid={`print-up-${c.key}`}><ArrowUp size={13} /></button>
                        <button type="button" className="pct-m-btn" onClick={moveDown} disabled={idx === printOrder.length - 1} title="Muta in jos" data-testid={`print-down-${c.key}`}><ArrowDown size={13} /></button>
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="print-info">
                Va fi printat cu filtrele si sortarea curente (max 1000 randuri). Format: A4 landscape. Latimea coloanelor se recalculeaza automat proportional cu valorile alese.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPrintOpen(false)}>Anuleaza</button>
              <button data-testid="print-preview-btn" className="btn btn-ghost" onClick={() => doPrint(false)} disabled={printBusy || printCols.size === 0} title="Deschide raportul pentru vizualizare fara a printa">
                <Eye size={15} /> Preview
              </button>
              <button data-testid="print-execute-btn" className="btn btn-primary" onClick={() => doPrint(true)} disabled={printBusy || printCols.size === 0}>
                <Printer size={15} /> {printBusy ? "Se pregateste…" : "Printeaza"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cleanupOpen && (
        <div className="modal-backdrop" onClick={() => setCleanupOpen(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="cleanup-modal">
            <div className="modal-header">
              <div>
                <div className="modal-title"><Wand2 size={16} style={{verticalAlign:"-2px"}}/> Curata duplicate</div>
                <div className="modal-sub">
                  {cleanupGroups.length > 0
                    ? `Grup ${cleanupIdx + 1} / ${cleanupGroups.length} · ${cleanupGroups.length} grupuri detectate`
                    : "Nu mai sunt grupuri"}
                </div>
              </div>
              <button className="icon-btn" onClick={() => setCleanupOpen(false)}><X size={18} /></button>
            </div>

            <div className="cleanup-toolbar">
              <div className="seg">
                <button className={`seg-btn ${cleanupType === "name" ? "on" : ""}`} onClick={() => openCleanup("name")} data-testid="cleanup-type-name">
                  <Users size={13} /> dupa nume (fuzzy)
                </button>
                <button className={`seg-btn ${cleanupType === "address" ? "on" : ""}`} onClick={() => openCleanup("address")} data-testid="cleanup-type-address">
                  <Copy size={13} /> dupa adresa exacta
                </button>
              </div>
              <div className="cleanup-nav">
                <button className="icon-btn" disabled={cleanupIdx === 0} onClick={() => setCleanupIdx(i => Math.max(0, i - 1))} data-testid="cleanup-prev"><ChevronLeft size={16}/></button>
                <span className="cleanup-nav-num">{cleanupGroups.length > 0 ? cleanupIdx + 1 : 0} / {cleanupGroups.length}</span>
                <button className="icon-btn" disabled={cleanupIdx >= cleanupGroups.length - 1} onClick={() => setCleanupIdx(i => Math.min(cleanupGroups.length - 1, i + 1))} data-testid="cleanup-next"><ChevronRight size={16}/></button>
              </div>
            </div>

            <div className="modal-body cleanup-body">
              {cleanupLoading ? (
                <div className="empty">Se incarca grupurile…</div>
              ) : cleanupGroups.length === 0 ? (
                <div className="empty">Nu au fost gasite grupuri de duplicate. Excelent!</div>
              ) : (() => {
                const g = cleanupGroups[cleanupIdx];
                if (!g) return null;
                return (
                  <>
                    <div className="group-header">
                      <div className="group-key">
                        <span className="group-label">Grup:</span> <b>{g.key}</b>
                      </div>
                      <div className="group-count">{g.count} variante</div>
                    </div>

                    <div className="cleanup-hint">
                      Selecteaza <span className="chip keep">◉ Pastreaza</span> pe randul principal si bifeaza <span className="chip remove">☐ Sterge</span> pe cele redundante. Restul raman intacte.
                    </div>

                    <div className="cleanup-list">
                      {g.items.map((it, i) => {
                        const isKeep = it.id === keepId;
                        const isRemove = removeIds.has(it.id);
                        const nonEmpty = Object.entries(it).filter(([k,v]) => v && v !== "" && k !== "id");
                        return (
                          <div key={it.id} className={`cleanup-card ${isKeep ? "keep" : ""} ${isRemove ? "remove" : ""}`} data-testid={`cleanup-item-${it.id}`}>
                            <div className="cleanup-card-head">
                              <div className="cleanup-controls">
                                <label className="cleanup-radio" title="Pastreaza acest client">
                                  <input type="radio" checked={isKeep} onChange={() => chooseKeep(it.id)} data-testid={`keep-${it.id}`} />
                                  <span>Pastreaza</span>
                                </label>
                                <label className="cleanup-check" title="Sterge acest client">
                                  <input type="checkbox" checked={isRemove} onChange={() => toggleRemove(it.id)} disabled={isKeep} data-testid={`remove-${it.id}`} />
                                  <span>Sterge</span>
                                </label>
                              </div>
                              <div className="cleanup-badges">
                                <span className="mini-badge">{nonEmpty.length} campuri</span>
                                {isKeep && <span className="mini-badge keep-b">principal</span>}
                              </div>
                            </div>
                            <div className="cleanup-card-body">
                              <div className="ck-name">{it.client || "—"}</div>
                              <div className="ck-highlight">
                                <div className="ck-hi-loc" title="Localitate">
                                  <span className="ck-hi-label">Localitate</span>
                                  <span className="ck-hi-val">{it.localitate || "—"}</span>
                                </div>
                                <div className="ck-hi-addr" title="Adresa verificare">
                                  <span className="ck-hi-label">Adresa</span>
                                  <span className="ck-hi-val">{it.adresa_verificare || "—"}</span>
                                </div>
                              </div>
                              <div className="ck-fields">
                                {nonEmpty.filter(([k]) => k !== "client" && k !== "localitate" && k !== "adresa_verificare").map(([k, v]) => {
                                  const label = COLUMNS.find(c => c.key === k)?.label || k;
                                  return (
                                    <div key={k} className="ck-field">
                                      <span className="ck-key">{label}:</span>
                                      <span className="ck-val">{v}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            {cleanupGroups.length > 0 && !cleanupLoading && (
              <div className="modal-footer cleanup-footer">
                <div className="merge-mode">
                  <label className={`mm-opt ${mergeMode === "merge" ? "on" : ""}`}>
                    <input type="radio" checked={mergeMode === "merge"} onChange={() => setMergeMode("merge")} data-testid="mode-merge" />
                    <div>
                      <div className="mm-title"><Merge size={12}/> Uneste apoi sterge</div>
                      <div className="mm-desc">Copiaza campurile ne-goale de la duplicate in cel pastrat, apoi sterge duplicatele.</div>
                    </div>
                  </label>
                  <label className={`mm-opt ${mergeMode === "delete_only" ? "on" : ""}`}>
                    <input type="radio" checked={mergeMode === "delete_only"} onChange={() => setMergeMode("delete_only")} data-testid="mode-delete" />
                    <div>
                      <div className="mm-title"><Trash2 size={12}/> Doar sterge duplicatele</div>
                      <div className="mm-desc">Nu copia nimic, doar sterge inregistrarile bifate.</div>
                    </div>
                  </label>
                </div>
                <div className="cleanup-actions">
                  <button className="btn btn-ghost" onClick={() => setCleanupOpen(false)}>Inchide</button>
                  <button className="btn btn-ghost" disabled={cleanupIdx >= cleanupGroups.length - 1} onClick={() => setCleanupIdx(i => i + 1)}>Sari (fara actiune)</button>
                  <button data-testid="do-merge-btn" className="btn btn-primary" disabled={mergeBusy || !keepId || removeIds.size === 0} onClick={doMerge}>
                    <Wand2 size={14} /> {mergeBusy ? "Se proceseaza…" : `Aplica (sterge ${removeIds.size})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {smsOpen && (
        <SmsModal
          clientIds={Array.from(selected)}
          onClose={() => setSmsOpen(false)}
          showToast={showToast}
          reloadClients={loadClients}
          currentUser={currentUser}
        />
      )}

      {borderouOpen && (
        <BorderouModal
          clientIds={borderouClientIds ?? Array.from(selected)}
          defaultNrs={(() => {
            // Auto NR crt per client (with gaps for deleted positions) for the borderou header.
            const nrs = {};
            if (borderouClientIds) {
              // "Borderou din filtru": ids are ordered - walk positions and skip deleted ones
              let pos = 1;
              let safety = 0;
              const maxSafety = borderouClientIds.length + deletedPositionsForFilter.size + 100000;
              for (const id of borderouClientIds) {
                while (deletedPositionsForFilter.has(pos) && safety < maxSafety) { pos++; safety++; }
                nrs[id] = pos;
                pos++;
                safety++;
              }
            } else {
              // Bulk-selected from visible items: use each row's known position
              for (const id of selected) {
                const row = items.find(r => r.id === id);
                const p = row?._pos ?? posMap[id];
                if (typeof p === "number") nrs[id] = p;
              }
            }
            return nrs;
          })()}
          onClose={() => { setBorderouOpen(false); setBorderouClientIds(null); }}
          showToast={showToast}
          isAdmin={isAdmin}
        />
      )}

      {toast && <div className={`toast ${toast.kind}`} data-testid="toast">{toast.msg}</div>}
    </div>
  );
}

function StatCard({ icon, label, value, testid, onClick, active }) {
  return (
    <div
      className={`stat-card ${onClick ? "clickable" : ""} ${active ? "active" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
    >
      <div className="stat-ico">{icon}</div>
      <div className="stat-body">
        <div className="stat-label">{label}</div>
        <div className="stat-value">{value}</div>
      </div>
    </div>
  );
}

// ============ Login Screen ============
function LoginScreen({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showChange, setShowChange] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("user", JSON.stringify(r.data.user));
      onLoggedIn(r.data.user);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Autentificare esuata");
    } finally { setBusy(false); }
  };

  return (
    <div className="login-shell" data-testid="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <div className="login-logo">
            <div className="brand-mark">
              <Flame size={28} strokeWidth={2.2} />
            </div>
          </div>
          <div>
            <div className="login-title">VIMATO INSTALATII</div>
            <div className="login-sub">Verificari & Revizii Gaz · Registru Clienti</div>
          </div>
        </div>

        <label className="login-field">
          <span>Utilizator</span>
          <div className="login-input">
            <User size={15} />
            <input
              data-testid="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              placeholder="admin"
            />
          </div>
        </label>

        <label className="login-field">
          <span>Parola</span>
          <div className="login-input">
            <Lock size={15} />
            <input
              data-testid="login-password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Parola"
            />
            <button type="button" className="login-eye" onClick={() => setShowPw(v => !v)}>
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </label>

        {err && <div className="login-err" data-testid="login-error">{err}</div>}

        <button className="btn btn-primary login-btn" disabled={busy || !username || !password} data-testid="login-submit">
          <LogIn size={15} /> {busy ? "Se autentifica…" : "Autentificare"}
        </button>

        <button
          type="button"
          className="login-link"
          onClick={() => setShowChange(true)}
          data-testid="open-change-pw-from-login"
        >
          <Key size={12} /> Schimba parola
        </button>

        <div className="login-hint">
          Contacteaza administratorul pentru credentiale.<br/>
          (parola trebuie schimbata la primul login)
        </div>
      </form>

      {showChange && (
        <LoginChangePasswordModal
          initialUsername={username}
          onClose={() => setShowChange(false)}
          onDone={(newPw) => {
            setShowChange(false);
            setPassword(newPw);
            setErr("");
          }}
        />
      )}
    </div>
  );
}

// ============ Change Password from Login (no JWT required) ============
function LoginChangePasswordModal({ initialUsername, onClose, onDone }) {
  const [username, setUsername] = useState(initialUsername || "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    if (!username.trim()) { setErr("Introdu numele utilizatorului"); return; }
    if (newPw.length < 6) { setErr("Parola noua trebuie sa aiba minim 6 caractere"); return; }
    if (newPw !== confirmPw) { setErr("Confirmarea parolei nu corespunde"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password-unauthenticated`, {
        username: username.trim(),
        old_password: oldPw,
        new_password: newPw,
      });
      setOk(true);
      setTimeout(() => onDone(newPw), 1500);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Schimbarea parolei a esuat");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal modal-sm" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="login-change-pw-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><Key size={16} style={{ verticalAlign: "-2px" }}/> Schimba parola</div>
            <div className="modal-sub">Foloseste parola veche pentru a seta una noua</div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {ok ? (
            <div className="empty" style={{ color: "var(--ok)", padding: 20 }}>
              Parola a fost schimbata. Te loghezi automat...
            </div>
          ) : (
            <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
              <label className="field"><span className="lbl">Utilizator</span>
                <input data-testid="cpw-username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ex: laura, tania1" />
              </label>
              <label className="field"><span className="lbl">Parola veche</span>
                <input data-testid="cpw-old" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
              </label>
              <label className="field"><span className="lbl">Parola noua (min 6 caractere)</span>
                <input data-testid="cpw-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
              </label>
              <label className="field"><span className="lbl">Confirma parola noua</span>
                <input data-testid="cpw-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
              </label>
              {err && <div className="login-err" data-testid="cpw-error">{err}</div>}
            </div>
          )}
        </div>
        {!ok && (
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Anuleaza</button>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="cpw-submit">
              <Key size={14}/> {busy ? "Se schimba…" : "Schimba parola"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

// ============ Change Password Modal ============
function ChangePasswordModal({ forced, onClose, onDone }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (newPw.length < 6) { setErr("Parola noua trebuie sa aiba minim 6 caractere"); return; }
    if (newPw !== confirmPw) { setErr("Parolele nu se potrivesc"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: oldPw, new_password: newPw });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={forced ? undefined : onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><Key size={16} style={{ verticalAlign: "-2px" }}/> Schimba parola</div>
            {forced && <div className="modal-sub" style={{ color: "#f59e0b" }}>Obligatoriu la primul login</div>}
          </div>
          {!forced && <button className="icon-btn" onClick={onClose}><X size={18} /></button>}
        </div>
        <div className="modal-body">
          <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
            <label className="field"><span className="lbl">Parola veche</span>
              <input data-testid="old-password" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} /></label>
            <label className="field"><span className="lbl">Parola noua</span>
              <input data-testid="new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></label>
            <label className="field"><span className="lbl">Confirma parola noua</span>
              <input data-testid="confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} /></label>
          </div>
          {err && <div className="login-err" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          {!forced && <button className="btn btn-ghost" onClick={onClose}>Anuleaza</button>}
          <div style={{ flex: 1 }} />
          <button data-testid="submit-change-password" className="btn btn-primary" onClick={submit} disabled={busy}>
            <Save size={14} /> {busy ? "Se salveaza…" : "Salveaza"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Users Management Modal ============
function UsersModal({ onClose, showToast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // for edit dialog
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // { id, username, new_password, force_change }
  const [form, setForm] = useState({ username: "", password: "", full_name: "", role: "tehnician" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data.items);
    } catch (e) { showToast("Eroare useri", "err"); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    if (!form.username || form.password.length < 6) {
      showToast("Username + parola (min 6 caractere)", "err");
      return;
    }
    try {
      await axios.post(`${API}/users`, form);
      showToast(`User ${form.username} creat`);
      setCreating(false);
      setForm({ username: "", password: "", full_name: "", role: "tehnician" });
      load();
    } catch (e) { showToast(e.response?.data?.detail || "Eroare", "err"); }
  };

  const saveEdit = async () => {
    try {
      const payload = { role: editing.role, full_name: editing.full_name, active: editing.active };
      if (editing.new_password) payload.new_password = editing.new_password;
      await axios.put(`${API}/users/${editing.id}`, payload);
      showToast("User actualizat");
      setEditing(null);
      load();
    } catch (e) { showToast(e.response?.data?.detail || "Eroare", "err"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Sterge userul "${u.username}"?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      showToast("User sters");
      load();
    } catch (e) { showToast(e.response?.data?.detail || "Eroare", "err"); }
  };

  const doReset = async () => {
    if (!resetting || (resetting.new_password || "").length < 6) {
      showToast("Parola trebuie sa aiba minim 6 caractere", "err");
      return;
    }
    try {
      await axios.put(`${API}/users/${resetting.id}`, {
        new_password: resetting.new_password,
        must_change_password: !!resetting.force_change,
      });
      showToast(`Parola pentru ${resetting.username} a fost resetata`);
      setResetting(null);
      load();
    } catch (e) { showToast(e.response?.data?.detail || "Eroare", "err"); }
  };

  const suggestPw = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let s = "";
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="users-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><Users size={16} style={{ verticalAlign: "-2px" }}/> Utilizatori</div>
            <div className="modal-sub">Admin poate crea/modifica/sterge useri si stabili roluri</div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="filter-badge">{users.length} useri</div>
            <button className="btn btn-primary" onClick={() => setCreating(true)} data-testid="add-user-btn">
              <UserPlus size={14} /> Adauga user
            </button>
          </div>

          {loading ? <div className="empty">Se incarca…</div> : (
            <table className="mini-grid">
              <thead>
                <tr>
                  <th>Username</th><th>Nume</th><th>Rol</th><th>Activ</th><th>Actiuni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.username}`}>
                    <td><b>{u.username}</b></td>
                    <td>{u.full_name || <span className="muted">—</span>}</td>
                    <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                    <td>{u.active ? <span className="ok-dot">●</span> : <span className="off-dot">●</span>}</td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() => setResetting({ id: u.id, username: u.username, new_password: "", force_change: true })}
                        data-testid={`reset-pw-${u.username}`}
                        title="Reseteaza parola"
                      ><Key size={14}/></button>
                      <button className="icon-btn" onClick={() => setEditing({ ...u })} data-testid={`edit-user-${u.username}`}><Edit3 size={14}/></button>
                      <button className="icon-btn danger" onClick={() => del(u)} data-testid={`delete-user-${u.username}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && (
        <div className="modal-backdrop" onClick={() => setCreating(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">User nou</div>
              <button className="icon-btn" onClick={() => setCreating(false)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <label className="field"><span className="lbl">Username (3-32 caractere, litere mici/cifre)</span>
                  <input data-testid="new-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></label>
                <label className="field"><span className="lbl">Parola (min 6 caractere)</span>
                  <input data-testid="new-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
                <label className="field"><span className="lbl">Nume complet</span>
                  <input data-testid="new-user-fullname" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></label>
                <label className="field"><span className="lbl">Rol</span>
                  <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="admin">admin (acces total)</option>
                    <option value="tehnician">tehnician (edit + SMS)</option>
                    <option value="viewer">viewer (doar citire)</option>
                  </select></label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>Anuleaza</button>
              <button className="btn btn-primary" onClick={createUser} data-testid="submit-new-user"><UserPlus size={14}/> Creaza</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Editeaza {editing.username}</div>
              <button className="icon-btn" onClick={() => setEditing(null)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <label className="field"><span className="lbl">Nume complet</span>
                  <input value={editing.full_name || ""} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} /></label>
                <label className="field"><span className="lbl">Rol</span>
                  <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                    <option value="admin">admin</option>
                    <option value="tehnician">tehnician</option>
                    <option value="viewer">viewer</option>
                  </select></label>
                <label className="field"><span className="lbl">Activ</span>
                  <select value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })}>
                    <option value="1">Da</option><option value="0">Nu (dezactivat)</option>
                  </select></label>
                <label className="field"><span className="lbl">Parola noua (optional)</span>
                  <input type="password" placeholder="Lasa gol daca nu schimbi" value={editing.new_password || ""} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })} /></label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditing(null)}>Anuleaza</button>
              <button className="btn btn-primary" onClick={saveEdit}><Save size={14}/> Salveaza</button>
            </div>
          </div>
        </div>
      )}
      {resetting && (
        <div className="modal-backdrop" onClick={() => setResetting(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} data-testid="reset-password-dialog">
            <div className="modal-header">
              <div>
                <div className="modal-title"><Key size={16} style={{ verticalAlign: "-2px" }}/> Resetare parola</div>
                <div className="modal-sub">Utilizator: <b>{resetting.username}</b></div>
              </div>
              <button className="icon-btn" onClick={() => setResetting(null)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <label className="field">
                  <span className="lbl">Parola noua (min 6 caractere)</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      data-testid="reset-new-password"
                      type="text"
                      autoFocus
                      style={{ flex: 1 }}
                      value={resetting.new_password}
                      onChange={(e) => setResetting({ ...resetting, new_password: e.target.value })}
                      placeholder="ex: Parola123"
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setResetting({ ...resetting, new_password: suggestPw() })}
                      title="Genereaza parola random"
                    >Sugereaza</button>
                  </div>
                </label>
                <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    data-testid="force-change-checkbox"
                    checked={!!resetting.force_change}
                    onChange={(e) => setResetting({ ...resetting, force_change: e.target.checked })}
                  />
                  <span className="lbl" style={{ marginBottom: 0 }}>Cere schimbarea parolei la primul login</span>
                </label>
                <div className="muted" style={{ fontSize: 11 }}>
                  Comunica userului parola noua verbal sau prin SMS. Actiunea va aparea in Istoric modificari.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setResetting(null)}>Anuleaza</button>
              <button className="btn btn-primary" onClick={doReset} data-testid="confirm-reset-pw">
                <Key size={14}/> Reseteaza parola
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Audit Log Modal ============
function AuditModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 100;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/audit`, { params: { action: actionFilter, skip: page * limit, limit } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [actionFilter, page]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="audit-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><History size={16} style={{ verticalAlign: "-2px" }}/> Istoric modificari</div>
            <div className="modal-sub">{total} evenimente inregistrate</div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
            <select className="select" value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0); }} data-testid="audit-action-filter">
              <option value="">Toate actiunile</option>
              <option value="login">Login</option>
              <option value="create_client">Creare client</option>
              <option value="update_client">Modificare client</option>
              <option value="delete_client">Stergere client</option>
              <option value="bulk_delete">Stergere in bloc</option>
              <option value="merge_clients">Merge duplicate</option>
              <option value="import_clients">Import Excel</option>
              <option value="sms_send">Trimitere SMS</option>
              <option value="create_user">Creare user</option>
              <option value="update_user">Modificare user</option>
              <option value="delete_user">Stergere user</option>
            </select>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-primary"
              data-testid="export-audit-btn"
              onClick={() => {
                axios.get(`${API}/audit/export`, { responseType: "blob" })
                  .then((res) => {
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `istoric_modificari_${new Date().toISOString().slice(0,10)}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                  });
              }}
              title="Descarca Excel cu tot istoricul de modificari + rezumat per user"
            >
              <Download size={13} style={{ marginRight: 4 }} /> Descarca istoric
            </button>
          </div>
          <table className="mini-grid">
            <thead>
              <tr>
                <th>Data</th><th>User</th><th>Actiune</th><th>Entitate</th><th>Detalii</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="empty">Se incarca…</td></tr> :
                items.length === 0 ? <tr><td colSpan={5} className="empty">Nici o inregistrare</td></tr> :
                items.map(a => (
                  <tr key={a.id}>
                    <td className="tab">{new Date(a.at).toLocaleString("ro-RO")}</td>
                    <td><b>{a.username}</b></td>
                    <td><span className="action-badge">{a.action}</span></td>
                    <td>{a.entity_type} {a.entity_id ? <span className="muted">· {a.entity_id.substring(0, 8)}</span> : ""}</td>
                    <td className="details">{a.details && Object.keys(a.details).length > 0 ? JSON.stringify(a.details) : "—"}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Anterior</button>
          <span style={{ padding: "0 12px", fontSize: 12, color: "var(--text-dim)" }}>Pagina {page + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
          <button className="btn btn-ghost" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Urmator ›</button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Inchide</button>
        </div>
      </div>
    </div>
  );
}

// ============ Backup Modal ============
function BackupModal({ onClose, showToast }) {
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/backups`)
      .then((r) => { setItems(r.data.items || []); setRetention(r.data.retention_days || 30); })
      .catch(() => showToast && showToast("Nu pot incarca lista de backup-uri", "err"))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const createNow = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/backups/create`);
      showToast && showToast(`Backup creat: ${r.data.rows} clienti`, "ok");
      load();
    } catch {
      showToast && showToast("Backup esuat", "err");
    } finally {
      setCreating(false);
    }
  };

  const downloadFile = (filename) => {
    const token = localStorage.getItem("token");
    // Fetch as blob to include auth header, then trigger download
    axios.get(`${API}/backups/${encodeURIComponent(filename)}/download`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      })
      .catch(() => showToast && showToast("Descarcare esuata", "err"));
  };

  const deleteGroup = async (it) => {
    if (!window.confirm(`Sterg backup-ul din ${new Date(it.created_at).toLocaleString("ro-RO")}?`)) return;
    try {
      await axios.delete(`${API}/backups/${it.timestamp}/${it.reason}`);
      showToast && showToast("Backup sters", "ok");
      load();
    } catch {
      showToast && showToast("Stergere esuata", "err");
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const reasonLabel = (r) => {
    if (r === "daily") return "🕑 Zilnic auto";
    if (r === "manual") return "👤 Manual";
    if (r === "pre-bulk-delete") return "⚠️ Pre-stergere in bloc";
    if (r.startsWith("sync-parte-")) return `📦 Sync producție (${r.replace("sync-parte-", "").replace("-din-", "/")})`;
    if (r.startsWith("raport-")) return `📊 Raport ${r.replace("raport-", "").replace(/-/g, " ")}`;
    return r;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="backup-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><Database size={16} style={{ verticalAlign: "-2px" }} /> Backup baza de date</div>
            <div className="modal-sub">
              Zilnic la 02:00 · Retentie {retention} zile · Excel (.xlsx) + Dump MongoDB (.tar.gz) · Auto inainte de stergere in bloc
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-primary" onClick={createNow} disabled={creating} data-testid="create-backup-btn">
              <RotateCw size={14} style={{ marginRight: 6, animation: creating ? "spin 1s linear infinite" : "none" }} />
              {creating ? "Se creeaza…" : "Creaza backup acum"}
            </button>
            <button className="btn btn-ghost" onClick={load} disabled={loading} data-testid="refresh-backups-btn">
              Reincarca
            </button>
            <div style={{ flex: 1 }} />
            <span className="muted" style={{ fontSize: 12 }}>{items.length} snapshot-uri disponibile</span>
          </div>
          <table className="mini-grid" data-testid="backup-list">
            <thead>
              <tr>
                <th>Data</th>
                <th>Tip</th>
                <th>Excel</th>
                <th>Dump Mongo</th>
                <th style={{ width: 90 }}>Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="empty">Se incarca…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="empty">Nici un backup inca. Apasa „Creaza backup acum".</td></tr>
              ) : items.map((it) => (
                <tr key={`${it.timestamp}_${it.reason}`}>
                  <td className="tab">{new Date(it.created_at).toLocaleString("ro-RO")}</td>
                  <td>{reasonLabel(it.reason)}</td>
                  <td>
                    {it.xlsx ? (
                      <button className="link-btn" onClick={() => downloadFile(it.xlsx)} title="Descarca Excel" data-testid={`dl-xlsx-${it.timestamp}`}>
                        <Download size={12} /> {fmtSize(it.xlsx_size)}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td>
                    {it.dump ? (
                      <button className="link-btn" onClick={() => downloadFile(it.dump)} title="Descarca dump MongoDB" data-testid={`dl-dump-${it.timestamp}`}>
                        <Download size={12} /> {fmtSize(it.dump_size)}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td>
                    <button className="icon-btn danger" onClick={() => deleteGroup(it)} title="Sterge backup" data-testid={`del-backup-${it.timestamp}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <div className="muted" style={{ fontSize: 11, flex: 1 }}>
            Fisierele sunt stocate in <code>/app/backups/</code>. Backup-urile mai vechi de {retention} zile sunt sterse automat.
          </div>
          <button className="btn btn-primary" onClick={onClose}>Inchide</button>
        </div>
      </div>
    </div>
  );
}

// ============ Print History Modal ============
function PrintHistoryModal({ onClose, showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/borderou/history`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => showToast && showToast("Nu pot incarca istoricul", "err"))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const rebuild = async (it) => {
    setRebuilding(it.id);
    try {
      const res = await axios.post(`${API}/borderou/rebuild/${it.id}`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Borderou_${it.nr || 'rebuild'}_${(it.data || '').replace(/\./g, "")}.docx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      showToast && showToast(`Borderou cu ${it.count} clienti redescarcat`, "ok");
    } catch (e) {
      showToast && showToast(e.response?.data?.detail || "Redescarcare esuata", "err");
    } finally {
      setRebuilding(null);
    }
  };

  const totalPrintari = items.length;
  const totalClienti = items.reduce((s, i) => s + (i.count || 0), 0);
  const canRebuildCount = items.filter(i => i.can_rebuild).length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="print-history-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><Printer size={16} style={{ verticalAlign: "-2px" }}/> Istoric printari borderou</div>
            <div className="modal-sub">
              {totalPrintari} borderouri · {totalClienti} clienti listati · {canRebuildCount} pot fi redescarcati
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <table className="mini-grid" data-testid="print-history-list">
            <thead>
              <tr>
                <th>Data & Ora</th>
                <th>User</th>
                <th>Nr. Înregistrare</th>
                <th>Data Borderou</th>
                <th style={{ textAlign: "right" }}>Clienți</th>
                <th style={{ width: 130 }}>Acțiune</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="empty">Se incarca…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="empty">Nu ai printat borderouri inca</td></tr>
              ) : items.map((it) => (
                <tr key={it.id}>
                  <td className="tab">{new Date(it.at).toLocaleString("ro-RO")}</td>
                  <td><b>{it.username}</b></td>
                  <td>{it.nr || <span className="muted">—</span>}</td>
                  <td className="tab">{it.data}</td>
                  <td style={{ textAlign: "right" }}><b>{it.count}</b></td>
                  <td>
                    {it.can_rebuild ? (
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "4px 10px", fontSize: 11 }}
                        onClick={() => rebuild(it)}
                        disabled={rebuilding === it.id}
                        data-testid={`rebuild-${it.id}`}
                        title="Redescarca acelasi borderou cu aceiasi clienti"
                      >
                        <Download size={12} /> {rebuilding === it.id ? "..." : "Redescarca"}
                      </button>
                    ) : (
                      <span className="muted" style={{ fontSize: 10 }} title="Borderou vechi - lista de clienti nu a fost salvata in audit log">
                        (indisponibil)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <div className="muted" style={{ fontSize: 11, flex: 1 }}>
            Borderourile printate de acum inainte pot fi redescarcate identic. Cele vechi nu au lista de clienti salvata.
          </div>
          <button className="btn btn-primary" onClick={onClose}>Inchide</button>
        </div>
      </div>
    </div>
  );
}

// ============ SMS Modal ============
function SmsModal({ clientIds, onClose, showToast, reloadClients, currentUser }) {
  const [templates, setTemplates] = useState(SMS_TEMPLATES);
  const [kind, setKind] = useState("notificare");
  const [template, setTemplate] = useState(SMS_TEMPLATES.notificare.text);
  const [previews, setPreviews] = useState([]);
  const [previewStats, setPreviewStats] = useState({ total: 0, valid: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState({});
  const [savingTpl, setSavingTpl] = useState(false);
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    axios.get(`${API}/sms/config`).then(r => setConfig(r.data)).catch(() => setConfig({ configured: false }));
    // Load persisted templates from backend
    axios.get(`${API}/settings/sms-templates`).then(r => {
      const t = r.data.templates || SMS_TEMPLATES;
      setTemplates(t);
      const firstKey = Object.keys(t)[0];
      const activeKey = t[kind] ? kind : firstKey;
      setKind(activeKey);
      setTemplate(t[activeKey].text);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/sms/preview`, { client_ids: clientIds, template, kind });
      setPreviews(r.data.previews);
      setPreviewStats({ total: r.data.total, valid: r.data.valid });
    } catch (e) { showToast("Eroare preview", "err"); }
    finally { setLoading(false); }
  }, [clientIds, template, kind, showToast]);

  useEffect(() => { doPreview(); }, [doPreview]);

  const chooseKind = (k) => {
    setKind(k);
    setTemplate(templates[k].text);
  };

  const openEditor = () => {
    // Deep clone templates for editing
    setEditDraft(JSON.parse(JSON.stringify(templates)));
    setEditMode(true);
  };
  const cancelEditor = () => { setEditMode(false); setEditDraft({}); };
  const saveEditor = async () => {
    // Validate
    for (const [k, v] of Object.entries(editDraft)) {
      if (!v.label?.trim() || !v.text?.trim()) {
        showToast(`Sablonul '${k}' are label sau text gol`, "err");
        return;
      }
    }
    setSavingTpl(true);
    try {
      const r = await axios.put(`${API}/settings/sms-templates`, { templates: editDraft });
      const saved = r.data.templates;
      setTemplates(saved);
      const activeKey = saved[kind] ? kind : Object.keys(saved)[0];
      setKind(activeKey);
      setTemplate(saved[activeKey].text);
      showToast("Sabloane salvate", "ok");
      setEditMode(false);
    } catch (e) {
      showToast(e.response?.data?.detail || "Eroare la salvare", "err");
    } finally { setSavingTpl(false); }
  };
  const addNewTemplate = () => {
    let n = 1;
    while (editDraft[`custom_${n}`]) n++;
    setEditDraft({ ...editDraft, [`custom_${n}`]: { label: `Sablon nou ${n}`, text: "Scrie mesajul aici. Foloseste {client}, {data_verificare_2_ani}, etc." } });
  };
  const deleteTemplate = (k) => {
    if (Object.keys(editDraft).length <= 1) {
      showToast("Trebuie sa ramana cel putin un sablon", "err");
      return;
    }
    const next = { ...editDraft };
    delete next[k];
    setEditDraft(next);
  };

  const send = async () => {
    if (!window.confirm(`Trimit SMS catre ${previewStats.valid} clienti cu numar valid? (${previewStats.total - previewStats.valid} vor fi sariti)`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sms/send`, { client_ids: clientIds, template, kind });
      setResult(r.data);
      if (reloadClients) reloadClients();
    } catch (e) { showToast(e.response?.data?.detail || "Eroare trimitere", "err"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="sms-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><MessageSquare size={16} style={{ verticalAlign: "-2px" }}/> Trimite SMS</div>
            <div className="modal-sub">{clientIds.length} clienti selectati · {previewStats.valid} cu numar valid</div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {config && !config.configured && (
            <div className="warn-box">
              <AlertTriangle size={14} /> Twilio nu e configurat complet (lipseste Auth Token sau numar From).
              SMS-urile vor fi salvate ca <b>mocked</b> in istoric fara a fi trimise real.
            </div>
          )}

          <div className="sms-tpl-toolbar">
            <div className="seg">
              {Object.entries(templates).map(([k, v]) => (
                <button key={k} className={`seg-btn ${kind === k ? "on" : ""}`} onClick={() => chooseKind(k)} data-testid={`sms-kind-${k}`}>
                  {k === "notificare" ? <Send size={13}/> : <RotateCw size={13}/>} {v.label}
                </button>
              ))}
            </div>
            {isAdmin && (
              <button className="btn btn-ghost btn-sm" onClick={openEditor} data-testid="sms-edit-templates" title="Editeaza sabloanele SMS">
                <Edit3 size={13} /> Editeaza sabloane
              </button>
            )}
            <div className="sms-vars">Placeholders: {"{client}"} {"{data_verificare_2_ani}"} {"{data_revizie_10_ani}"} {"{localitate}"} {"{nr_telefon}"}</div>
          </div>

          <textarea
            className="sms-textarea"
            data-testid="sms-template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={5}
          />
          <div className="sms-hint">{template.length} caractere · {Math.ceil(template.length / 160)} SMS/client</div>

          <div className="sms-preview-list">
            <div className="sms-preview-head">
              <span>Preview ({previews.length})</span>
              <span>{previewStats.valid} cu telefon valid · {previewStats.total - previewStats.valid} vor fi sariti</span>
            </div>
            {loading ? <div className="empty">Preview…</div> :
              previews.slice(0, 10).map(p => (
                <div key={p.id} className={`sms-preview-row ${!p.valid_phone ? "invalid" : ""}`}>
                  <div className="sms-p-head">
                    <b>{p.client}</b>
                    <span className="sms-p-phone">{p.phone_e164 || <span className="muted">{p.phone_raw || "fara telefon"}</span>}</span>
                  </div>
                  <div className="sms-p-msg">{p.message}</div>
                </div>
              ))}
            {previews.length > 10 && <div className="empty" style={{ padding: 8 }}>… si inca {previews.length - 10} clienti</div>}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Anuleaza</button>
          <div style={{ flex: 1 }} />
          <button data-testid="send-sms-btn" className="btn btn-primary" onClick={send} disabled={busy || previewStats.valid === 0}>
            <Send size={14} /> {busy ? "Se trimite…" : `Trimite catre ${previewStats.valid} clienti`}
          </button>
        </div>

        {result && (
          <div className="sms-result-overlay">
            <div className="sms-result">
              <div className="sms-result-title">
                {result.mocked ? <><AlertTriangle size={14}/> MODUL MOCKED</> : <><FileCheck size={14}/> SMS trimise</>}
              </div>
              <div className="sms-result-stats">
                <div><b className="ok">{result.sent}</b> trimise</div>
                <div><b className="err">{result.failed}</b> esuate</div>
                <div><b className="muted-v">{result.skipped}</b> sarite</div>
              </div>
              {result.mocked && (
                <p className="import-help">Twilio nu e configurat complet. SMS-urile au fost logate ca "mocked" - vezi in istoric dupa activarea Twilio.</p>
              )}
              <button className="btn btn-primary" onClick={() => { setResult(null); onClose(); }} data-testid="sms-result-close">
                Am inteles
              </button>
            </div>
          </div>
        )}

        {editMode && (
          <div className="tpl-editor-overlay" onClick={cancelEditor}>
            <div className="tpl-editor" onClick={(e) => e.stopPropagation()} data-testid="tpl-editor">
              <div className="tpl-editor-head">
                <div>
                  <div className="modal-title"><Edit3 size={16} style={{ verticalAlign: "-2px" }}/> Editeaza sabloane SMS</div>
                  <div className="modal-sub">Modificarile sunt salvate in baza si vizibile pentru toti utilizatorii</div>
                </div>
                <button className="icon-btn" onClick={cancelEditor}><X size={18} /></button>
              </div>
              <div className="tpl-editor-body">
                <div className="tpl-hint">
                  <b>Placeholders disponibili:</b> {"{client}"}, {"{data_verificare_2_ani}"}, {"{data_revizie_10_ani}"}, {"{data_pif}"}, {"{localitate}"}, {"{adresa_verificare}"}, {"{nr_telefon}"}, {"{email}"}, {"{serie_contor}"}, {"{deg}"}
                </div>
                {Object.entries(editDraft).map(([k, v]) => (
                  <div key={k} className="tpl-card" data-testid={`tpl-card-${k}`}>
                    <div className="tpl-card-head">
                      <input
                        className="tpl-label-input"
                        value={v.label}
                        onChange={(e) => setEditDraft(d => ({ ...d, [k]: { ...d[k], label: e.target.value } }))}
                        placeholder="Nume sablon"
                        data-testid={`tpl-label-${k}`}
                      />
                      <span className="tpl-key">cheie: <code>{k}</code></span>
                      <button
                        className="icon-btn danger"
                        onClick={() => deleteTemplate(k)}
                        disabled={Object.keys(editDraft).length <= 1}
                        title="Sterge sablonul"
                        data-testid={`tpl-delete-${k}`}
                      ><Trash2 size={14} /></button>
                    </div>
                    <textarea
                      className="tpl-textarea"
                      rows={4}
                      value={v.text}
                      onChange={(e) => setEditDraft(d => ({ ...d, [k]: { ...d[k], text: e.target.value } }))}
                      data-testid={`tpl-text-${k}`}
                    />
                    <div className="tpl-meta">
                      <span>{v.text.length} caractere · ~{Math.ceil(v.text.length / 160)} SMS</span>
                    </div>
                  </div>
                ))}
                <button className="btn btn-ghost btn-block" onClick={addNewTemplate} data-testid="tpl-add-new">
                  <UserPlus size={14} /> Adauga sablon nou
                </button>
              </div>
              <div className="tpl-editor-foot">
                <button className="btn btn-ghost" onClick={cancelEditor} disabled={savingTpl}>Anuleaza</button>
                <button className="btn btn-primary" onClick={saveEditor} disabled={savingTpl} data-testid="tpl-save">
                  {savingTpl ? "Se salveaza…" : "Salveaza sabloane"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Borderou Settings Modal (admin only) ============
function BorderouSettingsModal({ initial, onClose, onSaved, showToast }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/borderou/settings`, form);
      showToast("Setari salvate");
      onSaved(form);
    } catch (e) { showToast(e.response?.data?.detail || "Eroare", "err"); }
    finally { setBusy(false); }
  };

  const fld = (key, label, hint) => (
    <label className="field">
      <span className="lbl">{label}</span>
      <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`bs-${key}`} />
      {hint && <span style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{hint}</span>}
    </label>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="borderou-settings-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">Setari borderou</div>
            <div className="modal-sub">Datele firmei si autorizatia care apar in antetul borderoului</div>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label className="field span-2">
              <span className="lbl">Titlu borderou</span>
              <textarea rows={2} value={form.titlu || ""} onChange={(e) => setForm({ ...form, titlu: e.target.value })} data-testid="bs-titlu" />
            </label>
            {fld("firma_nume", "Nume firma", "ex. SC VIMATO SRL")}
            {fld("beneficiar", "Beneficiar (destinatar)", "ex. Delgaz Grid S.A.")}
            {fld("sediu_localitate", "Sediu - localitate")}
            {fld("sediu_strada", "Sediu - strada")}
            {fld("sediu_numar", "Sediu - numar")}
            {fld("autorizatie_tip", "Autorizatie ANRE - tip", "ex. EDIB, EDIA")}
            {fld("autorizatie_nr", "Autorizatie ANRE - nr.", "ex. 21552/13.10.2027")}
            {fld("reprezentant", "Reprezentant legal")}
            {fld("functie_reprezentant", "Functie", "ex. Administrator")}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Anuleaza</button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="bs-save">
            <Save size={14} /> {busy ? "Salveaza…" : "Salveaza setari"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Borderou predare (Anexa 2) ============
function BorderouModal({ clientIds, defaultNrs, onClose, showToast, isAdmin }) {
  const today = new Date().toISOString().slice(0, 10);
  const [nrSolicitare, setNrSolicitare] = useState("");
  const [data, setData] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // Show NR crt column by default when we have system-computed NRs (gaps preserved)
  const [showNrInreg, setShowNrInreg] = useState(!!(defaultNrs && Object.keys(defaultNrs).length > 0));
  const [nrInregs, setNrInregs] = useState(() => {
    if (!defaultNrs) return {};
    // Convert numeric values to strings for the input fields
    const m = {};
    Object.entries(defaultNrs).forEach(([id, v]) => { if (v != null) m[id] = String(v); });
    return m;
  });

  useEffect(() => {
    axios.get(`${API}/borderou/settings`).then(r => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.all(
          clientIds.map(id => axios.get(`${API}/clients/${id}`).then(r => r.data).catch(() => null))
        );
        const ordered = results.filter(Boolean);
        const withTip = ordered.map(c => {
          const hasVerif = !!(c.efectuat_verificare && String(c.efectuat_verificare).trim());
          const hasRev = !!(c.efectuat_revizie && String(c.efectuat_revizie).trim());
          let tip;
          if (hasVerif && hasRev) tip = "VIU/RIU";
          else if (hasRev) tip = "RIU";
          else if (hasVerif) tip = "VIU";
          else tip = (c.data_revizie_10_ani && !c.data_verificare_2_ani) ? "RIU" : "VIU";
          return { ...c, tip };
        });
        setRows(withTip);
      } catch (e) {
        showToast("Eroare la incarcare", "err");
      } finally { setLoading(false); }
    })();
  }, [clientIds, showToast]);

  const toggleTip = (idx) => {
    // Cycle: VIU -> RIU -> VIU/RIU -> VIU
    const nextOf = (t) => (t === "VIU" ? "RIU" : t === "RIU" ? "VIU/RIU" : "VIU");
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, tip: nextOf(r.tip) } : r));
  };

  const formatData = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };

  const downloadDocx = async () => {
    setBusy(true);
    try {
      const dataRo = formatData(data);
      const tips = {};
      rows.forEach(r => { tips[r.id] = r.tip; });
      const r = await axios.post(`${API}/borderou/docx`, {
        client_ids: rows.map(r => r.id),
        nr_solicitare: nrSolicitare,
        data: dataRo,
        tips,
        show_nr_inreg: showNrInreg,
        nr_inregs: showNrInreg ? nrInregs : {},
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const safeNr = (nrSolicitare || "borderou").replace(/[^a-zA-Z0-9._-]/g, "_");
      link.setAttribute("download", `Borderou_${safeNr}.docx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Borderou Word descarcat");
    } catch (e) {
      showToast(e.response?.data?.detail || "Eroare descarcare Word", "err");
    } finally { setBusy(false); }
  };

  const doPrint = () => {
    setBusy(true);
    try {
      const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;" }[c]));
      const dataRo = formatData(data);
      const s = settings || {};
      const titlu = s.titlu || "Borderou predare fise revizii/verificari tehnice instalatii de utilizare gaze naturale (Anexa 2)";
      const firma = s.firma_nume || "SC VIMATO SRL";
      const sediuLoc = s.sediu_localitate || "Timisoara";
      const sediuStr = s.sediu_strada || "str.Salcamilor";
      const sediuNr = s.sediu_numar || "8";
      const autTip = s.autorizatie_tip || "EDIB";
      const autNr = s.autorizatie_nr || "21552/13.10.2027";
      const rep = s.reprezentant || "Martin Viorel";
      const functie = s.functie_reprezentant || "Administrator";
      const beneficiar = s.beneficiar || "Delgaz Grid S.A.";

      const html = `<!doctype html><html lang="ro"><head>
        <meta charset="utf-8"/>
        <title>Borderou predare - ${esc(nrSolicitare)}</title>
        <style>
          @page { size: A4 portrait; margin: 18mm 15mm; }
          * { box-sizing: border-box; }
          body {
            font-family: "Times New Roman", Times, serif;
            color: #000;
            margin: 0;
            padding: 0;
            font-size: 12pt;
          }
          .doc { padding: 0; }
          .top-right {
            text-align: right;
            font-size: 13pt;
            font-weight: 700;
            line-height: 1.4;
            margin-bottom: 14px;
          }
          .top-right .lbl { font-size: 10pt; font-weight: 400; color: #333; }
          h1 {
            font-size: 12pt;
            text-align: center;
            font-weight: 700;
            margin: 0 0 18px 0;
            text-decoration: underline;
          }
          .intro {
            line-height: 1.7;
            text-align: justify;
            margin-bottom: 14px;
          }
          .intro .dots { letter-spacing: 0.5px; }
          .intro b { font-weight: 700; }
          .rc {
            font-weight: 700;
            font-size: 14pt;
            color: #000;
            letter-spacing: 0.5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #000;
            padding: 8px 10px;
            font-size: 11pt;
            vertical-align: middle;
          }
          th {
            background: #f4b8b8;
            font-weight: 700;
            text-align: left;
            font-size: 11pt;
          }
          td.center { text-align: center; font-weight: 700; }
          td.tip { text-align: center; font-weight: 700; }
          .signatures {
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
          }
          .sig-block { min-width: 220px; }
          .sig-title { font-weight: 700; margin-bottom: 40px; }
          .sig-line { border-top: 1px solid #000; padding-top: 3px; font-size: 10pt; text-align: center; }
          .noprint {
            position: fixed;
            top: 12px;
            right: 12px;
            background: #f59e0b;
            color: #111;
            border: none;
            padding: 10px 18px;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 12px;
            box-shadow: 0 4px 10px rgba(0,0,0,0.2);
            z-index: 100;
            font-family: sans-serif;
          }
          @media screen {
            body { background: #e5e7eb; padding: 24px; }
            .doc {
              background: white;
              max-width: 210mm;
              min-height: 297mm;
              margin: 0 auto;
              padding: 18mm 15mm;
              box-shadow: 0 4px 24px rgba(0,0,0,0.15);
            }
          }
          @media print {
            body { padding: 0; }
            .noprint { display: none !important; }
            tr { page-break-inside: avoid; }
          }
        </style>
      </head><body>
        <button class="noprint" onclick="window.print()">🖨 Printeaza</button>
        <div class="doc">
          <div class="top-right">
            <div><span class="lbl">Nr.</span> ${esc(nrSolicitare)}</div>
            <div><span class="lbl">Data</span> ${esc(dataRo)}</div>
          </div>
          <h1>${esc(titlu)}</h1>
          <p class="intro">
            <span class="dots">........................</span><b>${esc(firma)}</b><span class="dots">......................................................</span>...,
            având sediul social în ...${esc(sediuLoc)}.................${esc(sediuStr)}
            ..............................nr. .........<b>${esc(sediuNr)}</b>.....................<span class="dots">...</span>,
            având autorizația ANRE tip <u>${esc(autTip)}</u> nr. ...${esc(autNr)}...legal
            reprezentată prin <b>${esc(rep)}</b> în calitate de ${esc(functie)}..... Vă înaintăm un nr.de
            <b>${rows.length}</b> fișe de revizii/verificări.
          </p>
          <table>
            <thead>
              <tr>
                ${showNrInreg ? `<th style="width: 12%;">NR. INREG.</th>` : ""}
                <th style="width: ${showNrInreg ? "22%" : "24%"};">NUME /PRENUME</th>
                <th style="width: ${showNrInreg ? "14%" : "16%"};">LOCALITATE</th>
                <th style="width: ${showNrInreg ? "28%" : "32%"};">ADRESA</th>
                <th style="width: ${showNrInreg ? "16%" : "18%"};">COD LOC CONSUM</th>
                <th style="width: ${showNrInreg ? "8%" : "10%"};">VIU</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `<tr>
                ${showNrInreg ? `<td class="center">${esc(nrInregs[r.id] || "")}</td>` : ""}
                <td>${esc(r.client)}</td>
                <td>${esc(r.localitate)}</td>
                <td>${esc(r.adresa_verificare)}</td>
                <td>${esc(r.deg || "")}</td>
                <td class="tip">${esc(r.tip)}</td>
              </tr>`).join("")}
            </tbody>
          </table>
          <div class="signatures">
            <div class="sig-block">
              <div class="sig-title">Am predat,</div>
              <div class="sig-line">${esc(firma)}</div>
            </div>
            <div class="sig-block">
              <div class="sig-title">Am primit,</div>
              <div class="sig-line">${esc(beneficiar)}</div>
            </div>
          </div>
        </div>
        <script>window.addEventListener('load', () => setTimeout(() => window.print(), 500));</script>
      </body></html>`;
      const win = window.open("", "_blank");
      if (!win) { showToast("Blocare pop-up - permite din browser", "err"); setBusy(false); return; }
      // Safe alternative to document.write: use Blob URL
      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);
      win.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      showToast("Borderou generat");
      onClose();
    } catch (e) {
      showToast("Eroare la generare", "err");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="borderou-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title"><FileText size={16} style={{ verticalAlign: "-2px" }} /> Borderou predare (Anexa 2)</div>
            <div className="modal-sub">
              {clientIds.length} clienti selectati · fise revizii/verificari
              {settings && <> · antet: <b style={{color: "var(--accent)"}}>{settings.firma_nume}</b></>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {isAdmin && (
              <button className="icon-btn" onClick={() => setShowSettings(true)} title="Setari borderou (nume firma, autorizatie, reprezentant)" data-testid="open-borderou-settings">
                <Key size={16} />
              </button>
            )}
            <button className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div className="modal-body">
          <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
            <label className="field">
              <span className="lbl">Nr. solicitare</span>
              <input
                data-testid="borderou-nr"
                value={nrSolicitare}
                onChange={(e) => setNrSolicitare(e.target.value)}
                placeholder="ex. 166280"
              />
            </label>
            <label className="field">
              <span className="lbl">Data</span>
              <input
                data-testid="borderou-data"
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
              />
            </label>
          </div>

          <div className="borderou-preview">
            <div className="bp-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <span>Preview borderou</span>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  data-testid="toggle-nr-inregistrare"
                  checked={showNrInreg}
                  onChange={(e) => setShowNrInreg(e.target.checked)}
                />
                <span>Include coloana <b>Nr. Inregistrare</b></span>
              </label>
            </div>
            {loading ? <div className="empty">Se incarca…</div> : (
              <table className="mini-grid borderou-tbl">
                <thead>
                  <tr>
                    {showNrInreg && <th style={{ width: 130 }}>NR. INREG.</th>}
                    <th>NUME /PRENUME</th>
                    <th>LOCALITATE</th>
                    <th>ADRESA</th>
                    <th>COD LOC CONSUM</th>
                    <th style={{ width: 60 }}>VIU/RIU</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id}>
                      {showNrInreg && (
                        <td>
                          <input
                            className="nr-inreg-input"
                            data-testid={`nr-inreg-${r.id}`}
                            value={nrInregs[r.id] || ""}
                            onChange={(e) => setNrInregs(prev => ({ ...prev, [r.id]: e.target.value }))}
                            placeholder="—"
                          />
                        </td>
                      )}
                      <td>{r.client}</td>
                      <td>{r.localitate}</td>
                      <td>{r.adresa_verificare}</td>
                      <td>{r.deg || <span className="muted">—</span>}</td>
                      <td>
                        <button
                          className={`tip-btn ${r.tip === "VIU" ? "viu" : r.tip === "RIU" ? "riu" : "viu-riu"}`}
                          onClick={() => toggleTip(i)}
                          data-testid={`toggle-tip-${r.id}`}
                          title="Click pentru a schimba: VIU → RIU → VIU/RIU"
                        >
                          {r.tip}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="import-help" style={{ marginTop: 10 }}>
            <b>VIU</b> = Verificare Instalație Utilizare · <b>RIU</b> = Revizie Instalație Utilizare. Click pe eticheta pentru a comuta.
          </p>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Anuleaza</button>
          <div style={{ flex: 1 }} />
          <button
            data-testid="download-docx-btn"
            className="btn btn-ghost"
            onClick={downloadDocx}
            disabled={busy || loading || rows.length === 0}
            title="Salveaza ca fisier Word (.docx) editabil"
          >
            <Download size={14} /> Salveaza Word
          </button>
          <button
            data-testid="print-borderou-btn"
            className="btn btn-primary"
            onClick={doPrint}
            disabled={busy || loading || rows.length === 0}
          >
            <Printer size={14} /> Printeaza borderou
          </button>
        </div>

        {showSettings && settings && (
          <BorderouSettingsModal
            initial={settings}
            onClose={() => setShowSettings(false)}
            onSaved={(newSettings) => { setSettings(newSettings); setShowSettings(false); }}
            showToast={showToast}
          />
        )}
      </div>
    </div>
  );
}

// ============ Root App wrapper (auth gate) ============
function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  });
  const [showChangePw, setShowChangePw] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [showPrintHistory, setShowPrintHistory] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setCurrentUser(null);
      showToast("Sesiune expirata - autentifica-te din nou", "err");
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [showToast]);

  useEffect(() => {
    // Verify token still valid on mount
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/auth/me`).catch(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setCurrentUser(null);
    });
  }, []);

  useEffect(() => {
    if (currentUser?.must_change_password) setShowChangePw(true);
  }, [currentUser]);

  const onLoggedIn = (user) => setCurrentUser(user);
  const onLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
  };

  if (!currentUser) return <LoginScreen onLoggedIn={onLoggedIn} />;

  return (
    <>
      <AppMain
        currentUser={currentUser}
        onLogout={onLogout}
        onOpenChangePassword={() => setShowChangePw(true)}
        onOpenUsers={() => setShowUsers(true)}
        onOpenAudit={() => setShowAudit(true)}
        onOpenBackups={() => setShowBackups(true)}
        onOpenPrintHistory={() => setShowPrintHistory(true)}
      />
      {showChangePw && (
        <ChangePasswordModal
          forced={currentUser.must_change_password}
          onClose={() => setShowChangePw(false)}
          onDone={() => {
            setShowChangePw(false);
            const u = { ...currentUser, must_change_password: false };
            setCurrentUser(u);
            localStorage.setItem("user", JSON.stringify(u));
            showToast("Parola schimbata cu succes");
          }}
        />
      )}
      {showUsers && <UsersModal onClose={() => setShowUsers(false)} showToast={showToast} />}
      {showAudit && <AuditModal onClose={() => setShowAudit(false)} />}
      {showBackups && <BackupModal onClose={() => setShowBackups(false)} showToast={showToast} />}
      {showPrintHistory && <PrintHistoryModal onClose={() => setShowPrintHistory(false)} showToast={showToast} />}
      {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
    </>
  );
}

export default App;
